import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import {
    OrbitControls,
    TransformControls,
    Environment,
    ContactShadows,
    BakeShadows,
    Html,
    Line,
    Box,
} from '@react-three/drei';
import { tokens } from '@fluentui/react-components';
import * as THREE from 'three';

import InnerSelectingDialog from '../dialogs/InnerSelectingDialog';
import OuterSelectingDialog from '../dialogs/OuterSelectingDialog';
import Wall from './Wall';
import SingleCabinet, { PanelEdges } from './SingleCabinet3D'; // Pervadintas importas ir naudojamas
import { OverlayBtn } from './InternalComponents';
import { useFurnitureData } from '../hooks/useFurnitureData';
import UnifiedBase from './UnifiedBase';
import UnifiedTopPanel, { getTopPanelExtraBounds } from './UnifiedTopPanel';
import Measurements from './Measurements';
import FlatArrowHead, { MeetingArrows } from './ArrowHead';

const C_ARROW = '#1e293b';
// Baldo matmenų IR laisvumo tarpelių linijos/rodyklės dabar TA PATI juoda spalva
// (CLEARANCE_LINE_COLOR = FURNITURE_COLOR) — vartotojo prašymu, kad, kai abu
// rodomi kartu, jos susilietų į VIENĄ linija (pilna baldo ribose, punktyrinė už
// jų — žr. ClearanceOverlay pastabą žemiau). Ženkliuko (input badge) FONO spalva
// lieka atskira (CLEARANCE_COLOR — balta), kad vertė vis tiek atpažįstama iš
// karto kaip laisvumo tarpelis, ne baldo matmuo (juodas ženkliukas).
const FURNITURE_COLOR = '#111827';
const FURNITURE_TEXT_COLOR = '#ffffff';
const CLEARANCE_LINE_COLOR = FURNITURE_COLOR;
const CLEARANCE_COLOR = '#ffffff';
// Gylio rodyklės vaizdinis ilgis — fiksuotas, NEPRIKLAUSO nuo realaus gylio
// (žr. ClickUp "Gylio rodyklės... gali būti fiksuoto ilgio").
const DEPTH_ARROW_LEN = 0.3;
const CLEARANCE_TEXT_COLOR = '#374151';
const CLEARANCE_BORDER_COLOR = '#e5e7eb';

// Patalpos TIKRASIS priekinis kraštas (gilesnės iš kairės/dešinės sienos gylis) —
// ta pati logika, kaip Builder3D.jsx modulio lygio `getEffWallDepth` (Patalpos
// matmenims/žmogaus siluetui). Čia DUBLIUOTA, o ne importuota, nes Builder3D.jsx
// pats importuoja ŠĮ failą (FurnitureGroup) — importas priešinga kryptimi sukurtų
// ciklą. Keičiant šią formulę, pakeisti IR Builder3D.jsx kopiją.
const getEffWallDepth = (roomShape, wallDepth, wallDepthRight) => {
    const showLeftWall = roomShape === 1 || roomShape === 2;
    const showRightWall = roomShape === 1 || roomShape === 3;
    return (showLeftWall && showRightWall)
        ? Math.max(wallDepth || 60, wallDepthRight || 60)
        : showRightWall ? (wallDepthRight || 60) : (wallDepth || 60);
};

const ArrowHead = ({ position, dir }) => {
    const rotMap = {
        right: [0, 0, -Math.PI / 2],
        left:  [0, 0,  Math.PI / 2],
        up:    [0, 0,  0],
        down:  [Math.PI, 0, 0],
        fwd:   [-Math.PI / 2, 0, 0],
        back:  [Math.PI / 2, 0, 0],
    };
    const rot = rotMap[dir] || [0, 0, 0];
    return (
        <mesh position={position} rotation={rot}>
            <coneGeometry args={[0.012, 0.055, 6]} />
            <meshStandardMaterial color={C_ARROW} />
        </mesh>
    );
};

const Obstacle3D = ({ data, onEdit, isSelected }) => {
    const width = Math.max(0.001, Number(data.width) / 100 || 0);
    const height = Math.max(0.001, Number(data.height) / 100 || 0);
    const depth = Math.max(0.001, Number(data.depth) / 100 || 0);
    const initialX = (Number(data.x) / 100 || 0);
    const initialY = (Number(data.y) / 100 || 0);
    const initialZ = (Number(data.z) / 100 || 0);

    const handlePointerDown = (e) => {
        e.stopPropagation();
        if (onEdit) onEdit(data);
    };

    return (
        <group position={[initialX, initialY, initialZ]}>
            <mesh onClick={handlePointerDown} position={[width/2, height/2, depth/2]}>
                <boxGeometry args={[width, height, depth]} />
                <meshStandardMaterial color={isSelected ? "#ff9800" : "orange"} opacity={0.6} transparent />
            </mesh>
            <Html position={[width/2, height + 0.1, depth/2]} center>
                <div style={{ color: 'white', fontSize: '10px', background: 'rgba(0,0,0,0.5)', padding: '2px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {data.name || 'Kliūtis'}
                </div>
            </Html>
        </group>
    );
};


// Anchored to the room's real wall/floor lines (not the furniture's own edges) so these
// dimensions sit "iš karto už sienos/grindų" — just outside the wall/floor — instead of
// floating at a small fixed offset from the furniture, where they end up overlapping the
// clearance gap indicators whenever the furniture isn't flush against that wall/floor.
const FurnitureMeasurementsOverlay = ({ totalWidth, totalHeight, totalDepth, onDimensionChange, furnitureId, disabled = false, modalOpen = false, displayZ, position, wallWidth, hideInnerArrows = false, pixelOffsetM = 0.12, depthPixelOffsetM = 0.3, floorPixelOffsetM = 0.3, depthRowOffsetM = 0.4, marginZLocal = 0 }) => {
    const wallW = (wallWidth || 600) / 100;
    const floorY = -position.y - floorPixelOffsetM; // just below the real floor line (world y=0)
    // Gylio eilutės (Line/taškai/ženkliukas žemiau) SAVO Y, ATVIRKŠTINE tvarka nei
    // floorY — žr. depthRowOffsetM komentarą tėviniame komponente.
    const depthRowY = -position.y - depthRowOffsetM;
    const leftWallX = -position.x - pixelOffsetM;   // just left of the real left wall (world x=0)
    // rightWallX (gylio ženkliukas) NAUDOJA SAVO, didesnį postūmį — šalia nieko
    // nekonkuruoja (žr. depthPixelOffsetM komentarą prie jo apskaičiavimo), tad
    // neriboja jo tuo pačiu kukliu pixelOffsetM, kuris skirtas floorY/leftWallX.
    const rightWallX = (wallW - position.x) + depthPixelOffsetM; // just right of the real right wall

    return (
        <group position={[0, 0, displayZ]}>
            {/* Anksčiau šios rodyklės naudojo 3D kūgio geometriją (mesh+coneGeometry) —
                skirtingai nuo visų kitų (Patalpos/Laisvumo tarpelių) rodyklių, kurios yra
                plokščios 2D CSS trikampiai (<Html>). 3D kūgis, žiūrimas per perspektyvinę
                kamerą, atrodo "ištemptas/iškreiptas" priklausomai nuo kampo — dabar
                suvienodinta su ta pačia plokščia 2D FlatArrowHead, kaip ir visur kitur.
                /admin/bugs "Rodyklės pasislepia už sienų/grindų" — PATI linija (<Line>,
                tikra 3D geometrija, skirtingai nuo FlatArrowHead/Measurements, kurie abu
                <Html> ir todėl niekada neužstojami) liko normaliai depth-testinama, tad
                kai PATALPOS gylis didesnis už BALDO gylį (žr. measurementsZ komentarą
                aukščiau — rodyklių plokštuma rišasi prie baldo, ne patalpos gylio),
                šoninė siena (kuri driekiasi per visą PATALPOS gylį) fiziškai stovi ARČIAU
                kameros už šią liniją ir ją dengia. depthTest={'{false}'} + aukštas
                renderOrder verčia liniją visada pieštis VIRŠUJE, nepriklausomai nuo to,
                kas yra arčiau — vartotojo paties pasiūlytas sprendimas ("Bring forward"). */}
            <Line points={[[0, floorY, marginZLocal], [totalWidth, floorY, marginZLocal]]} color={FURNITURE_COLOR} lineWidth={1} depthTest={false} renderOrder={999} />
            {/* Kai kartu rodomi ir laisvumo tarpeliai, ŠIS galas sutampa su punktyrinės
                tarpelio linijos vidiniu galu — vietoj DVIEJŲ atskirai centruotų rodyklių
                (kurios vizualiai "užlipdavo" viena ant kitos), ClearanceOverlay tame
                taške nupiešia VIENĄ MeetingArrows ženklą (dvi "nosimis" susiliečiančios
                rodyklės), todėl čia savoji rodyklė tiesiog praleidžiama. */}
            {!hideInnerArrows && <FlatArrowHead position={[totalWidth, floorY, marginZLocal]} dir="right" color={FURNITURE_COLOR} />}
            {!hideInnerArrows && <FlatArrowHead position={[0, floorY, marginZLocal]} dir="left" color={FURNITURE_COLOR} />}
            <Measurements
                value={totalWidth}
                hideUnit={true}
                position={[totalWidth / 2, floorY, marginZLocal]}
                onChange={(val) => onDimensionChange(furnitureId, 'width', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
                title="Baldo plotis"
                bgColor={FURNITURE_COLOR}
                textColor={FURNITURE_TEXT_COLOR}
            />

            <Line points={[[leftWallX, 0, 0], [leftWallX, totalHeight, 0]]} color={FURNITURE_COLOR} lineWidth={1} depthTest={false} renderOrder={999} />
            {!hideInnerArrows && <FlatArrowHead position={[leftWallX, totalHeight, 0]} dir="up" color={FURNITURE_COLOR} />}
            {!hideInnerArrows && <FlatArrowHead position={[leftWallX, 0, 0]} dir="down" color={FURNITURE_COLOR} />}
            <Measurements
                value={totalHeight}
                hideUnit={true}
                position={[leftWallX, totalHeight / 2, 0]}
                onChange={(val) => onDimensionChange(furnitureId, 'height', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
                rotation={[0, 0, Math.PI / 2]}
                title="Baldo aukštis"
                bgColor={FURNITURE_COLOR}
                textColor={FURNITURE_TEXT_COLOR}
            />

            {/* Gylio (Z ašies) matmuo — kaip ir kitur, taškas ("dot"), ne rodyklė, nes
                kryptinis trikampis "į/iš ekrano" šioje projekcijoje neturėtų prasmės.
                Rodyklės ILGIS fiksuotas (ne proporcingas realiam gyliui) — tik pats
                SKAIČIUS keičiasi keičiant gylį, o rodyklė nei pailgėja, nei sutrumpėja
                (žr. ClickUp "Gylio rodyklės... gali būti fiksuoto ilgio").
                Y šiek tiek pakelta virš floorY (grindų linijos, ties kuria jau stovi
                pločio rodyklė/ženkliukas) — anksčiau abu ženkliukai susieidavo TIKSLIAI
                tame pačiame taške prie kampo (X=rightWallX ≈ totalWidth), vizualiai
                persidengdami (žr. ClickUp "2.0 Baldas. Gylio matmenų vieta. Truputį
                pagrąžinti."). */}
            <Line points={[[rightWallX, depthRowY + 0.06, marginZLocal], [rightWallX, depthRowY + 0.06, marginZLocal + DEPTH_ARROW_LEN]]} color={FURNITURE_COLOR} lineWidth={1} depthTest={false} renderOrder={999} />
            <FlatArrowHead position={[rightWallX, depthRowY + 0.06, marginZLocal + DEPTH_ARROW_LEN]} dir="dot" color={FURNITURE_COLOR} />
            <FlatArrowHead position={[rightWallX, depthRowY + 0.06, marginZLocal]} dir="dot" color={FURNITURE_COLOR} />

            {/* Ženkliuko Y (ne Z) atskirtas nuo abiejų taškų — kamera žiūri TIESIAI Z
                ašimi, tad Z=0 ir Z=DEPTH_ARROW_LEN taškai IR ženkliukas tarp jų (Z vidurys)
                suplokštėja į beveik TĄ PATĮ ekrano tašką (perspektyvos sutrumpėjimas), o
                fiksuoto pikselių dydžio ženkliuko kortelė persidengia su tašku, atrodydama
                sugadinta (žr. /admin/bugs "baldo matmenys pasislepia po patalpos sienomis
                ir grindimis" — iš tikrųjų tai nebuvo slėpimasis už sienos/grindų
                geometrijos (šios HTML kortelės niekada nėra jos "užstojamos"), o šis
                Z-suplokštėjimo persidengimas, kurį kameros pakeitimas TIK padidino, nes
                kamera dabar dažnai stovi toliau). Z ašis kamerai visada suplokštėja, tad
                tik Y atskyrimas (ne vien +0.06 aukščiau floorY) realiai išsprendžia. */}
            <Measurements
                value={totalDepth}
                hideUnit={true}
                position={[rightWallX, depthRowY - 0.16, marginZLocal + DEPTH_ARROW_LEN / 2]}
                onChange={(val) => onDimensionChange(furnitureId, 'depth', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
                title="Baldo gylis"
                bgColor={FURNITURE_COLOR}
                textColor={FURNITURE_TEXT_COLOR}
            />
        </group>
    );
};


const PositionMeasurements = ({ position, totalWidth, totalHeight, onMove, furnitureId, disabled = false, modalOpen = false, displayZ, groupRef }) => {
    const distanceLeft = position.x;
    const distanceBottom = position.y;

    // Rodyklės/linijos ir ženkliuko EKRANO pozicija lieka fiksuota (žr. komentarą
    // prie šio overlay renderinimo vietos — "rodyklės neturi judėti"), bet PATS
    // SKAIČIUS ženkliuke turi atsinaujinti gyvai tempimo metu, skaitant iš to
    // paties imperatyviai atnaujinamo groupRef.current.position, kurį naudoja ir
    // pats 3D modelis (žr. /admin/bugs "input laukuose esantys skaiciai
    // atsinaujina automatiskai pagal stumima"). useFrame čia TYČIA neatnaujina
    // liveX/liveY kiekvieną kadrą per setState, jei pozicija nepasikeitė (React
    // pats nutraukia re-renderį, kai naujas state === senas), tad kaina laisva
    // (idle) būsenoje šitas hook'as tik skaito, nieko nekeisdamas.
    const [liveLeft, setLiveLeft] = useState(distanceLeft);
    const [liveBottom, setLiveBottom] = useState(distanceBottom);
    useEffect(() => { setLiveLeft(distanceLeft); setLiveBottom(distanceBottom); }, [distanceLeft, distanceBottom]);
    useFrame(() => {
        if (!groupRef?.current) return;
        const gx = groupRef.current.position.x;
        const gy = groupRef.current.position.y;
        setLiveLeft(v => (v === gx ? v : gx));
        setLiveBottom(v => (v === gy ? v : gy));
    });

    const handleLeftChange = (val) => {
        onMove(furnitureId, val, position.y, position.z);
    };

    const handleBottomChange = (val) => {
        onMove(furnitureId, position.x, val, position.z);
    };

    return (
        <group position={[0, 0, displayZ]}>
            <Line points={[[0, totalHeight/2, 0], [-distanceLeft, totalHeight/2, 0]]} color="blue" lineWidth={1} dashed dashSize={0.1} gapSize={0.1} />
            <Measurements
                value={liveLeft}
                hideUnit={true}
                position={[-distanceLeft / 2, totalHeight/2 + 0.1, 0]}
                onChange={handleLeftChange}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />

            <Line points={[[totalWidth/2, 0, 0], [totalWidth/2, -distanceBottom, 0]]} color="blue" lineWidth={1} dashed dashSize={0.1} gapSize={0.1} />
            <Measurements
                value={liveBottom}
                hideUnit={true}
                position={[totalWidth/2 + 0.1, -distanceBottom / 2, 0]}
                onChange={handleBottomChange}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />
        </group>
    );
};

// ── MeasureTooltip ────────────────────────────────────────────────────────────
const MeasureTooltip = ({ label }) => (
    <div style={{
        position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
        transform: 'translateX(-50%)', background: '#1e293b', color: '#f8fafc',
        fontSize: 10, fontWeight: 500, padding: '3px 8px', borderRadius: 5,
        whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999,
        boxShadow: '0 4px 10px rgba(0,0,0,0.25)', fontFamily: 'sans-serif',
    }}>
        {label}
        <div style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
            borderTop: '4px solid #1e293b',
        }} />
    </div>
);

// ── ClearanceInput ────────────────────────────────────────────────────────────
const ClearanceInput = ({ value, position, onChange, modalOpen = false, title = '', bgColor = CLEARANCE_COLOR, textColor = CLEARANCE_TEXT_COLOR, borderColor = CLEARANCE_BORDER_COLOR }) => {
    const [localVal, setLocalVal] = useState((value * 100).toFixed(1));
    const [editing, setEditing] = useState(false);
    const [hovered, setHovered] = useState(false);
    useEffect(() => { if (!editing) setLocalVal((value * 100).toFixed(1)); }, [value, editing]);
    const commit = () => {
        setEditing(false);
        const n = parseFloat(localVal);
        if (!isNaN(n) && n >= 0 && onChange) onChange(n / 100);
        else setLocalVal((value * 100).toFixed(1));
    };
    return (
        <group position={position}>
            <Html center>
                <div style={{ position: 'relative', display: 'inline-flex' }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <div
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => { if (!modalOpen) setEditing(true); }}
                        style={{
                            pointerEvents: modalOpen ? 'none' : 'auto',
                            background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 4,
                            padding: '1px 5px', minWidth: 38, height: 20,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', userSelect: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        {editing ? (
                            <input
                                autoFocus
                                value={localVal}
                                onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setLocalVal(v); }}
                                onBlur={commit}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                onFocus={(e) => e.target.select()}
                                style={{ width: 38, border: 'none', outline: 'none', fontSize: 11, fontWeight: 500, color: textColor, background: 'transparent', textAlign: 'center', padding: 0, fontFamily: 'sans-serif' }}
                            />
                        ) : (
                            <span style={{ fontSize: 11, fontWeight: 500, color: textColor, whiteSpace: 'nowrap' }}>{localVal}</span>
                        )}
                    </div>
                    {hovered && title && <MeasureTooltip label={title} />}
                </div>
            </Html>
        </group>
    );
};

// ── ClearanceOverlay ──────────────────────────────────────────────────────────
// Shows clearance inputs (left, right, top, bottom, depth) around the active furniture.
// Balta spalva (CLEARANCE_COLOR), atitinkanti "Laisvumo tarpeliai" mygtuko spalvą.
const ClearanceOverlay = ({ position, totalWidth, totalHeight, totalDepth, wallWidth, wallHeight, wallDepth, onMoveXYZ, modalOpen = false, displayZ, hideInnerArrows = false, groupRef, pixelOffsetM = 0.12, floorPixelOffsetM = 0.3, marginZLocal = 0 }) => {
    const wallW = (wallWidth || 600) / 100;
    const wallH = (wallHeight || 250) / 100;
    const wallD = (wallDepth || 60) / 100;
    const leftGap   = Math.max(0, position.x);
    const rightGap  = Math.max(0, wallW - position.x - totalWidth);
    const bottomGap = Math.max(0, position.y);
    const topGap    = Math.max(0, wallH - position.y - totalHeight);
    const maxDepthOffset = Math.max(0, wallD - totalDepth);
    const frontGap  = Math.max(0, maxDepthOffset - position.z);
    const GAP_LINE = 0.001;

    // Rodyklės/linijos lieka fiksuotos (žr. komentarą žemiau), bet ClearanceInput
    // ženkliukų SKAIČIAI atsinaujina gyvai tempimo metu — ta pati logika, kaip
    // PositionMeasurements liveLeft/liveBottom aukščiau (žr. /admin/bugs "input
    // laukuose esantys skaiciai atsinaujina automatiskai pagal stumima").
    const [liveGaps, setLiveGaps] = useState({ left: leftGap, right: rightGap, bottom: bottomGap, top: topGap, front: frontGap });
    useEffect(() => {
        setLiveGaps({ left: leftGap, right: rightGap, bottom: bottomGap, top: topGap, front: frontGap });
    }, [leftGap, rightGap, bottomGap, topGap, frontGap]);
    useFrame(() => {
        if (!groupRef?.current) return;
        const gx = groupRef.current.position.x;
        const gy = groupRef.current.position.y;
        const gz = groupRef.current.position.z;
        const nextLeft = Math.max(0, gx);
        const nextRight = Math.max(0, wallW - gx - totalWidth);
        const nextBottom = Math.max(0, gy);
        const nextTop = Math.max(0, wallH - gy - totalHeight);
        const nextFront = Math.max(0, maxDepthOffset - gz);
        setLiveGaps(prev => (
            prev.left === nextLeft && prev.right === nextRight && prev.bottom === nextBottom && prev.top === nextTop && prev.front === nextFront
                ? prev
                : { left: nextLeft, right: nextRight, bottom: nextBottom, top: nextTop, front: nextFront }
        ));
    });
    // Tos pačios "bėgio" koordinatės, kaip FurnitureMeasurementsOverlay naudoja savo
    // pločio/aukščio linijoms (floorY pločio linijai, leftWallX aukščio linijai) — kad
    // laisvumo tarpelio punktyrinė linija būtų TA PATI linija su baldo matmenų linija,
    // prasidedanti lygiai ten, kur baigiasi baldo linija, o ne atskira lygiagreti linija
    // per vidurį (žr. "2.0 Baldas. Laisvumo tarpeliai" užduotį).
    const floorY = -position.y - floorPixelOffsetM;
    const leftWallX = -position.x - pixelOffsetM;

    // Kai tarpelis beveik lygus 0 (baldas glaudžiasi prie sienos/grindų/lubų), pilna
    // punktyrinė linija + rodyklių pora nebetelpa be persidengimo tame pačiame mažame
    // plote — TAČIAU rodyklės vis tiek turi likti matomos (o ne visiškai dingti, žr.
    // ClickUp "2.0 Baldas. Rodyklės išnyksta, jei laisvumas <2cm"), tad tokiu atveju
    // linija praleidžiama, bet vietoj jos parodomas tas pats kompaktiškas
    // "susiliečiančių nosimis" MeetingArrows ženklas, kuris jau naudojamas ten, kur
    // laisvumo rodyklė sutampa su baldo matmenų rodykle (hideInnerArrows atvejis).
    const MIN_VISIBLE_GAP = 0.01;

    // Near-zero fallback distance for a ClearanceInput label along its OWN dimension
    // line. When a furniture is pushed into a corner (e.g. left gap AND bottom gap
    // both ~0), the Left/Right label's Y is always floorY and the Bottom/Top label's
    // X is always leftWallX — and both of THOSE are the exact same "-0.12" constant
    // (see floorY/leftWallX above), so with the old fallback (0.16 / 0.10) the two
    // labels landed only ~4.5cm apart and visually overlapped (see ClickUp "2.0
    // Baldas. Laisvumo tarpelių vieta. Dabar būna uždengia, ar nesimato" — confirmed
    // live via Playwright: pushing a furniture flush left+bottom reproduces the exact
    // overlapping "0.0"/"0.0" pair from the reported screenshots). Moving BOTH
    // fallback values further from that shared 0.12 constant (in opposite axes)
    // grows the diagonal distance between the two labels — this doesn't touch the
    // dashed LINE positions at all (those must stay locked to floorY/leftWallX to
    // stay flush with FurnitureMeasurementsOverlay's line, per the comment above),
    // only how far the LABEL itself sits from the corner once its own gap is tiny.
    const NEAR_ZERO_LABEL_H = 0.28; // was 0.16 — left/right gap labels
    const NEAR_ZERO_LABEL_V = 0.22; // was 0.10 — bottom/top gap labels
    // /admin/bugs "Rodyklių/matmenų pozicija... laisvumo tarpeliai neaiškiai
    // išvaizduoti" — "Front clearance" (gylio) ženkliukas žemiau TYČIA naudoja TĄ
    // PATĮ X, kaip dešinės pusės pločio tarpelio ženkliukas (abu — totalWidth +
    // NEAR_ZERO_LABEL_H, kai rightGap mažas), skirdamiesi TIK 0,2m Y ašimi — kai
    // ABU tarpeliai (plotis IR gylis) yra maži vienu metu, šis 0,2m neužtenka, kad
    // du NEPRIKLAUSOMI ženkliukai (plius greta esantys baldo paties matmenų/pozicijos
    // ženkliukai) nebesusikirstų (patvirtinta vartotojo screenshot'ais: "0.3"/"60.0"
    // ženkliukai persidengia tame pačiame kampe). Savas, DIDESNIS X postūmis TIK šiam
    // ženkliukui (ne bendras su pločio ženkliuku) atskiria juos horizontaliai, ne tik
    // vertikaliai — dabar jie niekada nestovi tiesiai vienas po kitu.
    const NEAR_ZERO_LABEL_FRONT = 0.48;

    return (
        <group position={[0, 0, displayZ]}>
            {/* Left gap */}
            {leftGap > MIN_VISIBLE_GAP ? (<>
                <Line points={[[0, floorY, marginZLocal], [-(leftGap + GAP_LINE), floorY, marginZLocal]]} color={CLEARANCE_LINE_COLOR} lineWidth={1} dashed dashSize={0.02} gapSize={0.015} depthTest={false} renderOrder={999} />
                {hideInnerArrows
                    ? <MeetingArrows position={[0, floorY, marginZLocal]} orientation="horizontal" color={CLEARANCE_LINE_COLOR} />
                    : <FlatArrowHead position={[0, floorY, marginZLocal]} dir="right" color={CLEARANCE_LINE_COLOR} />}
                <FlatArrowHead position={[-(leftGap + GAP_LINE), floorY, marginZLocal]} dir="left" color={CLEARANCE_LINE_COLOR} />
            </>) : (
                <MeetingArrows position={[0, floorY, marginZLocal]} orientation="horizontal" color={CLEARANCE_LINE_COLOR} />
            )}
            <ClearanceInput value={liveGaps.left} position={[-(leftGap < 0.05 ? NEAR_ZERO_LABEL_H : leftGap / 2), floorY, marginZLocal]}
                onChange={(v) => onMoveXYZ(Math.max(0, Math.min(wallW - totalWidth, v)), position.y, position.z)} modalOpen={modalOpen}
                title="Laisvumas nuo kairės sienos" />

            {/* Right gap */}
            {rightGap > MIN_VISIBLE_GAP ? (<>
                <Line points={[[totalWidth, floorY, marginZLocal], [totalWidth + rightGap + GAP_LINE, floorY, marginZLocal]]} color={CLEARANCE_LINE_COLOR} lineWidth={1} dashed dashSize={0.02} gapSize={0.015} depthTest={false} renderOrder={999} />
                {hideInnerArrows
                    ? <MeetingArrows position={[totalWidth, floorY, marginZLocal]} orientation="horizontal" color={CLEARANCE_LINE_COLOR} />
                    : <FlatArrowHead position={[totalWidth, floorY, marginZLocal]} dir="left" color={CLEARANCE_LINE_COLOR} />}
                <FlatArrowHead position={[totalWidth + rightGap + GAP_LINE, floorY, marginZLocal]} dir="right" color={CLEARANCE_LINE_COLOR} />
            </>) : (
                <MeetingArrows position={[totalWidth, floorY, marginZLocal]} orientation="horizontal" color={CLEARANCE_LINE_COLOR} />
            )}
            <ClearanceInput value={liveGaps.right} position={[totalWidth + (rightGap < 0.05 ? NEAR_ZERO_LABEL_H : rightGap / 2), floorY, marginZLocal]}
                onChange={(v) => onMoveXYZ(Math.max(0, Math.min(wallW - totalWidth, wallW - totalWidth - v)), position.y, position.z)} modalOpen={modalOpen}
                title="Laisvumas nuo dešinės sienos" />

            {/* Bottom gap */}
            {bottomGap > MIN_VISIBLE_GAP ? (<>
                <Line points={[[leftWallX, 0, 0], [leftWallX, -(bottomGap + GAP_LINE), 0]]} color={CLEARANCE_LINE_COLOR} lineWidth={1} dashed dashSize={0.02} gapSize={0.015} depthTest={false} renderOrder={999} />
                {hideInnerArrows
                    ? <MeetingArrows position={[leftWallX, 0, 0]} orientation="vertical" color={CLEARANCE_LINE_COLOR} />
                    : <FlatArrowHead position={[leftWallX, 0, 0]} dir="up" color={CLEARANCE_LINE_COLOR} />}
                <FlatArrowHead position={[leftWallX, -(bottomGap + GAP_LINE), 0]} dir="down" color={CLEARANCE_LINE_COLOR} />
            </>) : (
                <MeetingArrows position={[leftWallX, 0, 0]} orientation="vertical" color={CLEARANCE_LINE_COLOR} />
            )}
            <ClearanceInput value={liveGaps.bottom} position={[leftWallX, -(bottomGap < 0.05 ? NEAR_ZERO_LABEL_V : bottomGap / 2), 0]}
                onChange={(v) => onMoveXYZ(position.x, Math.max(0, Math.min(wallH - totalHeight, v)), position.z)} modalOpen={modalOpen}
                title="Laisvumas nuo grindų" />

            {/* Top gap */}
            {topGap > MIN_VISIBLE_GAP ? (<>
                <Line points={[[leftWallX, totalHeight, 0], [leftWallX, totalHeight + topGap + GAP_LINE, 0]]} color={CLEARANCE_LINE_COLOR} lineWidth={1} dashed dashSize={0.02} gapSize={0.015} depthTest={false} renderOrder={999} />
                {hideInnerArrows
                    ? <MeetingArrows position={[leftWallX, totalHeight, 0]} orientation="vertical" color={CLEARANCE_LINE_COLOR} />
                    : <FlatArrowHead position={[leftWallX, totalHeight, 0]} dir="down" color={CLEARANCE_LINE_COLOR} />}
                <FlatArrowHead position={[leftWallX, totalHeight + topGap + GAP_LINE, 0]} dir="up" color={CLEARANCE_LINE_COLOR} />
            </>) : (
                <MeetingArrows position={[leftWallX, totalHeight, 0]} orientation="vertical" color={CLEARANCE_LINE_COLOR} />
            )}
            <ClearanceInput value={liveGaps.top} position={[leftWallX, totalHeight + (topGap < 0.05 ? NEAR_ZERO_LABEL_V : topGap / 2), 0]}
                onChange={(v) => onMoveXYZ(position.x, Math.max(0, Math.min(wallH - totalHeight, wallH - totalHeight - v)), position.z)} modalOpen={modalOpen}
                title="Laisvumas nuo lubų" />

            {/* Front clearance (Z) — distance from niche front edge; max = wallDepth - furnitureDepth */}
            <ClearanceInput value={liveGaps.front}
                position={[totalWidth + (rightGap < 0.05 ? NEAR_ZERO_LABEL_FRONT : rightGap / 2), floorY - 0.2, marginZLocal]}
                onChange={(v) => onMoveXYZ(position.x, position.y, Math.max(0, Math.min(maxDepthOffset, maxDepthOffset - v)))} modalOpen={modalOpen}
                title={`Atstumas nuo nišos priekio (maks. ${(maxDepthOffset * 100).toFixed(1)} cm)`} />
        </group>
    );
};

// Matmenų/laisvumo tarpelių rodyklės TYČIA naudoja STATIŠKĄ `position` state
// (renderinamos atskirame, ne gyvai sekamame `<group>` — žr. render'inimo
// vietą žemiau) — vartotojas paprašė, kad jos LIKTŲ FIKSUOTOS palei sienas
// per visą tempimo trukmę ir "pašoktų" į naują vietą tik paleidus pelę
// (onMove commit'ina naują position state), o judėtų TIK pats baldo 3D
// modelis (žr. /admin/bugs "linijos... turi būti fiksuotos palei sienas,
// o judėti tik pats baldas").

const WallMeasurementsOverlay = ({ width, height, depth, depthRight, roomShape, onDimensionChange, disabled = false, modalOpen = false, displayZ }) => {
    
    const showLeft = roomShape === 0 || roomShape === 1 || roomShape === 2;
    const showRight = roomShape === 1 || roomShape === 3;
    const safeDepthRight = depthRight || 60;

    return (
        <group position={[0, 0, displayZ]}>
            <Line points={[[0, -0.2, 0], [width/100, -0.2, 0]]} color="black" lineWidth={1} />
            <ArrowHead position={[width/100, -0.2, 0]} dir="right" />
            <ArrowHead position={[0, -0.2, 0]} dir="left" />
            <Measurements
                value={width / 100}
                position={[width / 100 / 2, -0.2, 0]}
                onChange={(val) => onDimensionChange(null, 'width', val)}
                hideUnit={true}
                disabled={disabled}
                modalOpen={modalOpen}
                htmlStyle={{ transform: 'translate(0, -12px)' }}
            />

            <Line points={[[-0.4, 0, 0], [-0.4, height/100, 0]]} color="black" lineWidth={1} />
            <ArrowHead position={[-0.4, height/100, 0]} dir="up" />
            <ArrowHead position={[-0.4, 0, 0]} dir="down" />
            <Measurements
                value={height / 100}
                position={[-0.4, height / 100 / 2, 0]}
                onChange={(val) => onDimensionChange(null, 'height', val)}
                hideUnit={true}
                disabled={disabled}
                modalOpen={modalOpen}
                htmlStyle={{ transform: 'translate(40px, 0)' }}
            />

            {showLeft && (
                <>
                    <Line points={[[-0.2, 0, 0], [-0.2, 0, depth/100]]} color="black" lineWidth={1} />
                    <ArrowHead position={[-0.2, 0, depth/100]} dir="fwd" />
                    <ArrowHead position={[-0.2, 0, 0]} dir="back" />
                    <Measurements
                        value={depth/100}
                        position={[-0.2, 0, depth/200]}
                        onChange={(val) => onDimensionChange(null, 'depth', val)}
                        hideUnit={true}
                        disabled={disabled}
                        modalOpen={modalOpen}
                        htmlStyle={{ transform: 'translate(64px, -26px)' }}
                    />
                </>
            )}

            {showRight && (
                <>
                    <Line points={[[width/100 + 0.2, 0, 0], [width/100 + 0.2, 0, safeDepthRight/100]]} color="black" lineWidth={1} />
                    <ArrowHead position={[width/100 + 0.2, 0, safeDepthRight/100]} dir="fwd" />
                    <ArrowHead position={[width/100 + 0.2, 0, 0]} dir="back" />
                    <Measurements
                        value={safeDepthRight/100}
                        position={[width/100 + 0.2, 0, safeDepthRight/200]}
                        onChange={(val) => onDimensionChange(null, 'depthRight', val)}
                        hideUnit={true}
                        disabled={disabled}
                        modalOpen={modalOpen}
                        htmlStyle={{ transform: 'translate(64px, -26px)' }}
                    />
                </>
            )}
        </group>
    );
};


const CameraAligner = ({ cabinets, basisHeight, onReady, currentComponentId, wallWidth, wallHeight, wallDepth, activeFurnitureId, activeStep, obstacles, wallThickness }) => {
    const { camera, controls, gl } = useThree();
    useLayoutEffect(() => {
        if (!controls || !gl) return;

        let allMinX = Infinity, allMaxX = -Infinity, allMinY = Infinity, allMaxY = -Infinity, allMinZ = Infinity, allMaxZ = -Infinity;

        allMinX = Math.min(allMinX, 0);
        allMaxX = Math.max(allMaxX, (wallWidth || 600) / 100);
        allMinY = Math.min(allMinY, 0);
        allMaxY = Math.max(allMaxY, (wallHeight || 250) / 100);
        allMinZ = Math.min(allMinZ, -wallThickness);
        allMaxZ = Math.max(allMaxZ, (wallDepth || 60) / 100);

        cabinets.forEach(cab => {
            allMinX = Math.min(allMinX, Number(cab.x) || 0);
            allMaxX = Math.max(allMaxX, (Number(cab.x) || 0) + (Number(cab.width) || 0));
            allMinY = Math.min(allMinY, Number(cab.y) || 0);
            allMaxY = Math.max(allMaxY, (Number(cab.y) || 0) + (Number(cab.height) || 0));
            allMinZ = Math.min(allMinZ, Number(cab.z) || 0);
            allMaxZ = Math.max(allMaxZ, (Number(cab.z) || 0) + (Number(cab.depth) || 0));
        });

        obstacles.forEach(obs => {
            const obsX = Number(obs.x) / 100 || 0;
            const obsY = Number(obs.y) / 100 || 0;
            const obsZ = Number(obs.z) / 100 || 0;
            const obsW = Number(obs.width) / 100 || 0;
            const obsH = Number(obs.height) / 100 || 0;
            const obsD = Number(obs.depth) / 100 || 0;

            allMinX = Math.min(allMinX, obsX);
            allMaxX = Math.max(allMaxX, obsX + obsW);
            allMinY = Math.min(allMinY, obsY);
            allMaxY = Math.max(allMaxY, obsY + obsH);
            allMinZ = Math.min(allMinZ, obsZ);
            allMaxZ = Math.max(allMaxZ, obsZ + obsD);
        });


        let centerX, centerY, centerZ;
        if (allMinX === Infinity) {
            centerX = (wallWidth / 100) / 2;
            centerY = (wallHeight / 100) / 2;
            centerZ = (wallDepth / 100) / 2;
        } else {
            centerX = allMinX + (allMaxX - allMinX) / 2;
            centerY = allMinY + (allMaxY - allMinY) / 2;
            centerZ = allMinZ + (allMaxZ - allMinZ) / 2;
        }


        // /admin/bugs "1.0 Patalpa. AutoZoom. Kai maži patalpos matmenys - AutoZoomina
        // - nebetelpa matmenys į ekraną" — šis skaičiavimas pritaiko kamerą TIK prie
        // 3D geometrijos ribų (kambario/baldo), bet matmenų/laisvumo tarpelių ženkliukai
        // (<Html> overlay'ai aplink baldą/kambarį) turi FIKSUOTĄ pikselių dydį, ne
        // mastelio. Dideliam kambariui tas fiksuotas "halo" aplink geometriją yra
        // nereikšminga ekrano dalis, bet labai mažam kambariui (pvz. 100×100cm,
        // vartotojo pateiktas pavyzdys) kamera priartėja taip, kad geometrija užpildo
        // beveik visą kadrą, ir ženkliukams, kurie turi stovėti ŠIEK TIEK už kambario
        // ribų, pikselių vietos nebelieka — jie "iškrenta" už matomos srities.
        // Sprendimas (vartotojo pačio pasiūlytas): neleisti EFEKTYVIAM (zoom'ui
        // naudojamam) plotiui/aukščiui susitraukti žemiau tam tikro minimumo, net jei
        // TIKRAS kambarys mažesnis — vartotojas mini savo įprastą 200×250cm matmenį
        // kaip atskaitos tašką; MIN_ZOOM_TARGET (1.5m) yra kuklesnis, bet pakankamas
        // minimumas, užtikrinantis, kad ženkliukų "halo" visada turės vietos kadre.
        const MIN_ZOOM_TARGET = 1.5;
        const targetWidth = Math.max(allMaxX - allMinX, MIN_ZOOM_TARGET);
        const targetHeight = Math.max(allMaxY - allMinY, MIN_ZOOM_TARGET);
        const targetDepth = allMaxZ - allMinZ;

        const canvasWidth = gl.domElement.clientWidth;
        const canvasHeight = gl.domElement.clientHeight;
        const canvasAspectRatio = canvasWidth / canvasHeight;

        const paddingFactor = 1.4;

        let distance;
        const verticalFov = (camera.fov * Math.PI) / 190;

        const distanceByHeight = (targetHeight / 2 / Math.tan(verticalFov / 2)) * paddingFactor;
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * canvasAspectRatio);
        const distanceByWidth = (targetWidth / 2 / Math.tan(horizontalFov / 2)) * paddingFactor;
        const distanceByDepth = targetDepth * paddingFactor;


        distance = Math.max(distanceByHeight, distanceByWidth, distanceByDepth);
        distance = Math.max(distance, 2);

        controls.target.set(centerX, centerY, centerZ);
        camera.position.set(centerX, centerY, centerZ + distance);
        camera.updateProjectionMatrix();
        controls.update();
        const timer = setTimeout(() => onReady(true), 50);
        return () => clearTimeout(timer);
    }, [cabinets, basisHeight, camera, controls, gl, onReady, currentComponentId, wallWidth, wallHeight, wallDepth, activeFurnitureId, activeStep, obstacles, wallThickness]);
    return null;
};

// ── SlidingDoor (single panel) ────────────────────────────────────────────────
// #19 hover animation: on hover the panel slides ~12% of its width toward opening direction
const SlidingDoor = ({ doorW, doorH, posX, posZ, panelThickness, plateColor, frameW, frameColor, handleSide }) => {
    const innerW = doorW - frameW * 2;
    const innerH = doorH - frameW * 2;
    const handleH = Math.max(0.10, Math.min(0.22, doorH * 0.18));
    const handleThick = 0.013;
    const handleProtrusion = 0.028;
    const handleY = doorH * 0.5;
    const handleX = handleSide === 'left'
        ? frameW + handleThick / 2 + 0.008
        : doorW - frameW - handleThick / 2 - 0.008;

    return (
        <group position={[posX, 0, posZ]}>
            {/* Inner panel (inset, shows selected plate colour) */}
            <Box args={[innerW, innerH, panelThickness * 0.82]} position={[doorW / 2, doorH / 2, 0]}>
                <meshStandardMaterial color={plateColor} roughness={0.42} metalness={0.06} />
            </Box>
            {/* Profile frame – top */}
            <Box args={[doorW, frameW, panelThickness]} position={[doorW / 2, doorH - frameW / 2, 0]}>
                <meshStandardMaterial color={frameColor} metalness={0.78} roughness={0.18} />
            </Box>
            {/* Profile frame – bottom */}
            <Box args={[doorW, frameW, panelThickness]} position={[doorW / 2, frameW / 2, 0]}>
                <meshStandardMaterial color={frameColor} metalness={0.78} roughness={0.18} />
            </Box>
            {/* Profile frame – left */}
            <Box args={[frameW, doorH, panelThickness]} position={[frameW / 2, doorH / 2, 0]}>
                <meshStandardMaterial color={frameColor} metalness={0.78} roughness={0.18} />
            </Box>
            {/* Profile frame – right */}
            <Box args={[frameW, doorH, panelThickness]} position={[doorW - frameW / 2, doorH / 2, 0]}>
                <meshStandardMaterial color={frameColor} metalness={0.78} roughness={0.18} />
            </Box>
            {/* Handle bar */}
            <Box args={[handleThick, handleH, handleProtrusion]} position={[handleX, handleY, panelThickness / 2 + handleProtrusion / 2]}>
                <meshStandardMaterial color={frameColor} metalness={0.92} roughness={0.08} />
            </Box>
        </group>
    );
};

// ── SlidingDoorsOverlay ───────────────────────────────────────────────────────
// Renders sliding door panels over the full cabinet width.
// Doors alternate between two Z-depth tracks so they can slide past each other.
// No panel ever exceeds the cabinet frame boundaries (X: 0 to totalWidth).
const SLIDING_PROFILES = [
    { frameW: 0.045, color: '#b2bac2' }, // Slim  – 45 mm aluminium
    { frameW: 0.070, color: '#8c959e' }, // Standard – 70 mm
    { frameW: 0.100, color: '#7a6355' }, // Wide  – 100 mm wood-imitation
];

const SlidingDoorsOverlay = ({ totalWidth, totalHeight, totalDepth, plateColor = '#d0d0d0', doorCount = 2, profile = 0, activeStep }) => {
    const trackH = 0.022;
    const trackD = 0.052;
    const panelThickness = 0.018;
    const planeGap = 0.005;   // distance between front and back Z plane

    const { frameW, color: frameColor } = SLIDING_PROFILES[profile] ?? SLIDING_PROFILES[0];
    const trackColor = '#a4acb4';

    // Door width: adds a small inter-door overlap so panels look like they lap each other,
    // but the overlap is kept within cabinet bounds.
    const overlapW = doorCount > 1 ? Math.min(0.06, totalWidth * 0.04) : 0;
    const doorW = totalWidth / doorCount + overlapW;
    const doorH = totalHeight - trackH * 2;

    // Two Z-depth planes (like a real 2-track system)
    const frontZ = totalDepth + panelThickness / 2 + 0.003;
    const backZ  = frontZ + panelThickness + planeGap;

    return (
        <group>
            {/* ── Top aluminium track ── */}
            <Box args={[totalWidth, trackH, trackD]}
                 position={[totalWidth / 2, totalHeight - trackH / 2, totalDepth - trackD / 2]}>
                <meshStandardMaterial color={trackColor} metalness={0.88} roughness={0.14} />
            </Box>
            {/* ── Bottom aluminium track ── */}
            <Box args={[totalWidth, trackH, trackD]}
                 position={[totalWidth / 2, trackH / 2, totalDepth - trackD / 2]}>
                <meshStandardMaterial color={trackColor} metalness={0.88} roughness={0.14} />
            </Box>
            {/* ── Groove lines on tracks (visual detail) ── */}
            {[frontZ, backZ].map((gz, gi) => (
                <Box key={gi} args={[totalWidth, 0.003, 0.004]}
                     position={[totalWidth / 2, trackH * 0.55, gz - panelThickness / 2 - 0.001]}>
                    <meshStandardMaterial color="#58606a" metalness={0.9} roughness={0.1} />
                </Box>
            ))}

            {/* ── Door panels ── */}
            {Array.from({ length: doorCount }).map((_, i) => {
                // Distribute doors so door[0] left edge = 0, door[N-1] right edge = totalWidth.
                // This guarantees no panel overflows the cabinet frame.
                const xPos = doorCount > 1
                    ? (i / (doorCount - 1)) * (totalWidth - doorW)
                    : (totalWidth - doorW) / 2;

                // Alternate between front and back tracks
                const zPos = i % 2 === 0 ? frontZ : backZ;

                // Handle faces the cabinet centre
                const panelCentre = xPos + doorW / 2;
                const handleSide = panelCentre <= totalWidth / 2 ? 'right' : 'left';

                return (
                    <SlidingDoor
                        key={i}
                        doorW={doorW}
                        doorH={doorH}
                        posX={xPos}
                        posZ={zPos}
                        panelThickness={panelThickness}
                        plateColor={plateColor}
                        frameW={frameW}
                        frameColor={frameColor}
                        handleSide={handleSide}
                    />
                );
            })}
        </group>
    );
};

const FurnitureGroup = ({
    furnitureId, cabinets, isActive, position, onSelect, onMove, allCabinets, // allCabinets yra VISI kabinetai scenoje
    activeStep, readOnly = false, onAddShelf, onRemoveShelf, onAddVertical, onRemoveVertical, onDimensionChange,
    wallWidth, wallHeight,
    wallDepth, wallDepthRight = 60, roomShape, onCabinetClick, selectedCabinetIndex,
    activeFurnitureId,
    allFurnitureGroupRects,
    onShelfHeightChange, onShelfDelete, onSectionDelete, onLeafSectionDelete, onNarySectionResize,
    coverPlates,
    modalOpen,
    onRemoveDoors,
    onCreateDoors,
    onCabinetWidthChange,
    onInterCabinetResize,
    onCabinetHeightChange,
    leftSideElement,
    rightSideElement,
    topPanelType = 0,
    topPanelHeight = 0.1,
    measureMode = 'furniture',
    onSplitSection, // Naujas prop'as
    splitSectionMode, // Naujas prop'as
    selectedShelf, // Perduodame selectedShelf, kad nustatytume, kur rodyti skaidymo mygtukus
    onHorizontalSplit,
    onVerticalSplit,
    onSectionRatioChange,
    onOpenOuterDialog,
    onOpenInnerDialog,
    mergedSections,
    mergeMode,
    onToggleMergeMode,
    onDetachSection,
    snapshotMode = false,
    furnitureType = 0,
    slidingDoorCount = 2,
    slidingDoorProfile = 0,
    onCollision = null,
    isColliding = false,
    selectedShelfBoard = null,
    onSetSelectedShelfBoard = null,
    selectedDividerKey = null,
    onDividerSelect = null,
    onClearSelections = null,
    onInteriorHSplit = null,
    onInteriorVSplit = null,
    onInteriorSectionResize = null,
    onInteriorSectionDelete = null,
    onInteriorLeafSectionDelete = null,
    onInteriorNarySectionResize = null,
    onPromoteAtPanel = null,
    onDemoteAtPanel = null,
    ...cabinetProps
}) => {
    const groupRef = useRef();
    const initialPositionRef = useRef(position);
    const lastSafePositionRef = useRef(position);
    const collisionTimerRef = useRef(null);
    const [collisionFlash, setCollisionFlash] = useState(false);
    const [lowDetail, setLowDetail] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const { controls, camera, gl, size } = useThree();

    // LOD: reduce detail when camera is far from furniture center
    const lowDetailRef = useRef(false);
    useFrame(() => {
        if (!camera) return;
        const cx = position.x + totalWidth / 2;
        const cy = position.y + totalHeight / 2;
        const cz = position.z + totalDepth / 2;
        const dist = Math.sqrt(
            (camera.position.x - cx) ** 2 +
            (camera.position.y - cy) ** 2 +
            (camera.position.z - cz) ** 2
        );
        const next = dist > 6;
        if (next !== lowDetailRef.current) {
            lowDetailRef.current = next;
            setLowDetail(next);
        }
    });

    useEffect(() => {
        lastSafePositionRef.current = position;
    }, [position]);

    const { totalWidth, totalHeight, totalDepth } = useMemo(() => {
        let currentTotalWidth = 0;
        let currentMaxHeight = 0;
        let currentMaxDepth = 0;

        cabinets.forEach(c => {
            currentTotalWidth += (Number(c.width) || 0);
            currentMaxHeight = Math.max(currentMaxHeight, (Number(c.height) || 0));
            currentMaxDepth = Math.max(currentMaxDepth, (Number(c.depth) || 0));
        });

        return {
            totalWidth: Math.max(0.001, currentTotalWidth),
            totalHeight: Math.max(0.001, currentMaxHeight),
            totalDepth: Math.max(0.001, currentMaxDepth)
        };
    }, [cabinets]);

    // Anksčiau buvo `wallDepth/100 + 0.01` — ŠIO baldo matmenų/laisvumo
    // tarpelių rodyklių grupė buvo pririšta prie PATALPOS gylio, nors ji
    // neturi nieko bendro su konkrečiu baldu. Keičiant patalpos gylį (kuris
    // niekaip nekeičia PAČIO baldo), rodyklių grupė vis tiek pasislinkdavo Z
    // ašimi, atrodydavo, kad rodyklės "išlenda į priekį"/atsiskiria nuo baldo
    // (žr. ClickUp "2.0 Baldas. Rodyklių/matmenų pozicijos fiksavimas. Dabar
    // būna patalpos matmenys išlenda į priekį"). Dabar pririšta prie PAČIO
    // baldo gylio — rodyklių plokštuma visada lygiai ties baldo priekiniu
    // paviršiumi (+1cm), nepriklausomai nuo patalpos gylio.
    // BANDYTA (ir ATŠAUKTA): duoti "Laisvumo tarpeliai" (ClearanceOverlay) SAVO,
    // prie patalpos priekio pririštą Z (per getEffWallDepth), kad "kiti
    // ismatavimai... turi rištis prie patalpos" — bet ClearanceOverlay IR
    // FurnitureMeasurementsOverlay YRA TYČIA viena plokštuma (žr. MeetingArrows/
    // hideInnerArrows aukščiau bei ClearanceOverlay faile — jų linijos/rodyklės
    // susitinka TIKSLIAI tuose pačiuose kampo taškuose). Asimetrinėje Nišoje
    // (pvz. kairė 60cm, dešinė 300cm) patalpos priekis ir baldo gylis skiriasi
    // ŠIMTAIS cm — atskyrus jų Z, dvi grupės, kurios turi susitikti kampe, ėmė
    // kaboti skirtinguose Z gyliuose, o linijos/rodyklės vizualiai "atsiskyrė"
    // (vartotojo pastebėta, screenshot). Grąžinta prie VIENOS bendros `measurementsZ`
    // abiem — jei "Laisvumo tarpeliai" vis tiek reikia rišti prie patalpos,
    // FurnitureMeasurementsOverlay turi judėti KARTU (negalima atskirti vienos be kitos).
    const measurementsZ = totalDepth + 0.01;

    // "Baldo matmenys" linijos TURI stovėti PRIE PAT "Patalpos matmenys" linijų —
    // vartotojas pažymėjo raudonai TIKSLIAI tai: baldo aukščio/pločio linijos turi
    // stovėti ŠALIA (ne "kažkur už sienos", o PRIE PAT) patalpos aukščio/pločio linijų
    // (žr. screenshot su raudonomis žymėmis). Pilna šios srities istorija (7 ankstesni
    // bandymai — per maža, per didelė, susikirto su Patalpos linija, "kolapso" klaidinga
    // diagnozė ir pataisymas skaičiavimu) — žr. [[feedback_camera_fit_contain_vs_cover]]
    // 7-10 punktus. Dabar, kai patikrinta (10 punktas), kad Patalpos linijos atstumas nuo
    // kameros (`roomMeasurementsZ`/`getEffWallDepth`) NESVYRUOJA keičiant gylį, saugu vėl
    // naudoti jos TIKRĄ apskaičiuotą offsetą kaip atskaitos tašką — ne nepriklausomai
    // spėlioti, o TIESIOGIAI pridėti nedidelį, ženkliuko dydį įskaičiuojantį tarpą
    // (žr. GAP_X_M/GAP_Y_M žemiau) PRIE jos, kad baldo linija atsidurtų TIKSLIAI ten,
    // kur vartotojas nupiešė.
    const verticalFovRad = (camera.fov * Math.PI) / 180;
    const worldPerPixelAt = (worldZ) => {
        const dist = Math.max(0.5, camera.position.z - worldZ);
        return (2 * dist * Math.tan(verticalFovRad / 2)) / Math.max(1, size.height);
    };
    const roomMeasurementsZWorld = getEffWallDepth(roomShape, wallDepth, wallDepthRight) / 100 + 0.01;
    const ROOM_PIXEL_GAP = 65; // ta pati konstanta, kaip Builder3D.jsx WallMeasurementsOverlay
    const roomPixelOffsetM = ROOM_PIXEL_GAP * worldPerPixelAt(roomMeasurementsZWorld);
    // BUVO fiksuota 0.08m pasaulio konstanta (taisyta praeitą kartą į pikselius), BET
    // net ir pikseliais, 10px tarpas TARP ŽENKLIUKŲ CENTRŲ (anchor taškų) neužtikrina,
    // kad patys ženkliukai (kurie TURI realų plotį/aukštį, žr. Measurements.jsx —
    // minWidth 35px/height 18px + padding/border) nepersidengtų — du ~50px pločio
    // ženkliukai, kurių centrai skiriasi vos 10px, VIS TIEK persidengs ~40px. Vartotojas:
    // "ar tu negali kazkaip fiksuoti ir skaiciuoti 3D objektu ir elementu koordinates ir
    // tada pagal jas uztikrinti kad niekada... nebutu overlap?" — t.y. tarpas turi būti
    // skaičiuojamas PAGAL PAČIŲ ženkliukų dydį, ne vien savavališkas pikselių skaičius.
    // Du ATSKIRI tarpai, pagal tai, kuria ašimi ženkliukai išsidėstę vienas šalia kito:
    // - GAP_X (aukščio linija, ženkliukai greta HORIZONTALIAI) — turi dengti abiejų
    //   ženkliukų PLOTĮ (~50px kiekvienas, plačiausias atvejis "250.0" tipo tekstui).
    // - GAP_Y (pločio/gylio eilutės, ženkliukai vienas PO KITU VERTIKALIAI) — turi
    //   dengti abiejų ženkliukų AUKŠTĮ (~20px kiekvienas, CSS height:18+padding/border).
    // Abiem atvejais PRIDEDAMAS vartotojo prašytas ~10px MATOMAS tarpas VIRŠ šio
    // minimalaus, ne-persidengiančio dydžio.
    const BADGE_WIDTH_PX = 38;
    const BADGE_HEIGHT_PX = 20;
    const VISIBLE_GAP_PX = 10;
    const GAP_Y_M = (BADGE_HEIGHT_PX + VISIBLE_GAP_PX) * worldPerPixelAt(roomMeasurementsZWorld);
    // BENDRA TAISYKLĖ (žr. Builder3D.jsx WallMeasurementsOverlay HEIGHT_LINE_OFFSET
    // komentarą — ta pati taisyklė IR TA PATI formulė, abi pusės, dubliuota kaip ir
    // `ROOM_PIXEL_GAP`): Baldo aukščio linija VISADA ARČIAU sienos už Patalpos liniją.
    // KLAIDA (rasta vartotojo screenshot'u — "baldo matmenys susikerta su patalpos
    // siena"): ankstesnis `WALL_CLEARANCE_PX=30` laikė sieną begalinio plonumo
    // plokštuma — bet siena TURI TIKRĄ 10cm storį (Wall.jsx `wt`), o ŠIS ženkliukas
    // (centruotas, pats turi plotį) savo artimiausiu kraštu nutolsta nuo SAVO inkaro
    // per pusę pločio — su senu postūmiu šis kraštas atsidurdavo VIDUJE sienos tūrio.
    // Teisinga: WALL_THICKNESS_M (tikra, pastovi pasaulio-metrų reikšmė) + ženklimo
    // PUSĖS pločio ir norimo matomo tarpo (pikseliais-pagrįsti).
    const WALL_THICKNESS_M = 0.1; // Wall.jsx "wt" konstantos kopija
    const pixelOffsetM = WALL_THICKNESS_M + (BADGE_WIDTH_PX / 2 + VISIBLE_GAP_PX) * worldPerPixelAt(roomMeasurementsZWorld);
    // Gylio ženkliukas (rightWallX, TIK FurnitureMeasurementsOverlay) neturi šalia
    // jokio konkuruojančio elemento iki pat žmogaus siluetо (~0.65m už sienos) — gali
    // naudoti ŽYMIAI didesnį, savarankišką postūmį (žr. 10 punktą).
    const overlayWorldZ = position.z + measurementsZ;
    const depthPixelOffsetM = Math.min(0.5, Math.max(0.25, 110 * worldPerPixelAt(overlayWorldZ)));

    // `floorY` elementai (pločio linija + gylio ženkliuko/taškų Y, bei Laisvumo
    // tarpelių kairės/dešinės/priekio ženkliukai) TURI STOVĖTI TARP grindų ir pačios
    // Patalpos pločio linijos — vartotojas aiškiai pažymėjo (raudonas stačiakampis +
    // "sukeisk vietomis su patalpos plocio matmeninimis, kad pilka linija butu
    // apacioje baldo matmenu"): Patalpos linija (pilka) visada IŠORINĖ/apatinė,
    // Baldo linija (juoda) — ARČIAU grindų, tarp jų ir Patalpos linijos.
    // ANKSČIAU bandyta (žr. [[feedback_camera_fit_contain_vs_cover]] naujausią punktą)
    // duoti šiai linijai DIDELĮ postūmį, kad jos EKRANO pozicija PRALENKTŲ Patalpos
    // liniją — tai IŠSPRENDĖ grindų kirtimą, bet davė NETEISINGĄ eiliškumą (baldo
    // linija atsidūrė UŽ Patalpos linijos, ne tarp jos ir grindų). Teisingas
    // sprendimas: PERKELTI šiuos elementus Z ašimi iš baldo paties (seklaus) gylio į
    // PATALPOS priekio Z (tą patį, kuriame stovi Patalpos linija) — tame pačiame Z
    // kaip ir Patalpos linija, nebereikia milžiniško postūmio vien dėl perspektyvos:
    // pakanka KUKLAUS, MAŽESNIO už `roomPixelOffsetM` dydžio, kad liktų tarp grindų
    // ir Patalpos linijos su aiškiu tarpu nuo abiejų. Gylio taškų/linijos PATI ilgio
    // reikšmė (DEPTH_ARROW_LEN) niekada nebuvo proporcinga realiam gyliui (žr. komentarą
    // ten) — tai grynai simbolinis ženklas su atskiru skaičiaus ženkliuku, tad Z
    // perkėlimas nekeičia jo prasmės, tik vietą ekrane.
    const marginZLocal = roomMeasurementsZWorld - (position.z + measurementsZ);
    const floorPixelOffsetM = Math.max(0.1, roomPixelOffsetM - GAP_Y_M);
    // Gylio (Z ašies) eilutė ("Baldo gylis" / "Patalpos gylis dešinė") — PRITAIKYTA
    // BENDRAI taisyklei (žr. pixelOffsetM/floorPixelOffsetM aukščiau bei Builder3D.jsx
    // depthLineY): Baldo VISADA ARČIAU (čia tiesiog roomPixelOffsetM, be papildomo
    // tarpo), Patalpos VISADA TOLIAU (Builder3D.jsx dabar prideda GAP_Y_M SAVO pusėje).
    // ANKSČIAU (vartotojo "Apkeisk pilka su juodu vietomis" prašymu) ši pora buvo vienintelė
    // su ATVIRKŠTINE (Patalpos arčiau) tvarka — vartotojas vėliau paprašė VIENOS bendros
    // taisyklės visoms trims poroms, tad ši kryptis dabar suvienodinta su pločio eilute.
    const depthRowOffsetM = roomPixelOffsetM;

    // Stogelis (topPanelType) gali fiziškai iškyšoti už totalWidth/totalHeight/
    // totalDepth (žr. UnifiedTopPanel.jsx) — sienos aukščio ribojimas ir kolizijos
    // su kitais baldais turi apie tai žinoti, kad baldas su Stogeliu "įtilptų" į
    // savo matmenis, o ne tyliai kyšotų pro lubą.
    const topPanelExtra = useMemo(() => getTopPanelExtraBounds(topPanelType, topPanelHeight), [topPanelType, topPanelHeight]);

    const cabinetOffsetsX = useMemo(() => {
        let currentXOffset = 0;
        return cabinets.map(cab => {
            const offset = currentXOffset;
            currentXOffset += (Number(cab.width) || 0);
            return offset;
        });
    }, [cabinets]);

    // Stogelis (kai topPanelType>0) anksčiau buvo piešiamas VIENĄ kartą per visą
    // grupę, ties topY=totalHeight (AUKŠČIAUSIO kabineto aukščiu) — kai baldą sudaro
    // keli skirtingo aukščio kabinetai (kiekvienas savo "skyrius"), žemesnysis
    // likdavo su tuščiu tarpu tarp savo paties viršaus ir dangos (vartotojas: "dabar
    // stogelis kabo ore ten kur skyriu aukstis yra mazesnis"). Čia sugrupuojami
    // GRETIMI (pagal eilės tvarką) kabinetai, turintys TĄ PATĮ aukštį, į vieną
    // "run'ą" — kiekvienam tokiam run'ui žemiau piešiamas ATSKIRAS UnifiedTopPanel,
    // su SAVO (ne grupės) pločiu ir aukščiu, tad danga visada tiksliai atsiremia į
    // SAVO run'o kabinetų viršų. Vienodo aukščio run'ai NESKAIDOMI papildomai — jų
    // danga lieka VIENA ištisinė plokštė, kaip ir anksčiau.
    const topPanelRuns = useMemo(() => {
        const runs = [];
        cabinets.forEach((cab, idx) => {
            const h = Number(cab.height) || 0;
            const w = Number(cab.width) || 0;
            const last = runs[runs.length - 1];
            if (last && Math.abs(last.height - h) < 0.0005) {
                last.width += w;
                last.endIdx = idx;
            } else {
                runs.push({ startIdx: idx, endIdx: idx, offsetX: cabinetOffsetsX[idx], width: w, height: h });
            }
        });
        return runs;
    }, [cabinets, cabinetOffsetsX]);


    const handleTransformChange = useCallback(() => {
        if (groupRef.current) {
            const currentTentativeX = groupRef.current.position.x;
            const currentTentativeY = groupRef.current.position.y;
            const currentTentativeZ = groupRef.current.position.z;

            const wallWidthMeters = (wallWidth || 600) / 100;
            const wallHeightMeters = (wallHeight || 250) / 100;
            const wallDepthMeters = (wallDepth || 60) / 100;


            // Stogelio iškyša (žr. topPanelExtra aukščiau) įskaičiuojama į ribojimą/koliziją,
            // bet NE į patį baldo pozicionavimą (totalWidth/2 centravimas ir pan. lieka
            // nepaliesti) — tik į tai, kiek toli baldas gali stumtis prieš atsitrenkdamas.
            const boundHeight = totalHeight + topPanelExtra.height;
            const boundWidth = totalWidth + topPanelExtra.width;
            const boundDepth = totalDepth + topPanelExtra.depth;

            const minX = 0;
            const maxX = Math.max(0, wallWidthMeters - boundWidth);
            const minY = 0;
            const maxY = Math.max(0, wallHeightMeters - boundHeight);
            const minZ = 0;
            const maxZ = Math.max(0, wallDepthMeters - boundDepth);

            let proposedX = Math.max(minX, Math.min(currentTentativeX, maxX));
            let proposedY = Math.max(minY, Math.min(currentTentativeY, maxY));
            let proposedZ = Math.max(minZ, Math.min(currentTentativeZ, maxZ));

            // Ankstesnė versija čia tik APTIKDAVO koliziją (bet kurioje ašyje) IR TADA
            // grąžindavo baldą TIKSLIAI į tą vietą, iš kurios pradėtas šis tempimo
            // kadras (lastSafePositionRef) — kadangi tas grąžinimas vykdavo TIK PO to,
            // kai pozicija jau būdavo (bent akimirkai) persidengianti, baldas galėdavo
            // vizualiai "prasmukti" per kaimyną prieš atšaukiant. Dabar kiekviena ašis
            // apkerpama ATSKIRAI IR IŠ KARTO — baldas "atsiremia" į kaimyno kraštą ir
            // sustoja tiksliai ties juo, niekada faktiškai neįeidamas į jo užimtą erdvę
            // (žr. "baldas gali judėti tik laisvoje erdvėje" užduotį).
            const others = allFurnitureGroupRects.filter(r => r.furnitureId !== furnitureId);
            const lastSafe = lastSafePositionRef.current;
            const EPS = 0.001; // 1mm tolerancija slankaus kablelio paklaidai (ta pati, kaip Layout.jsx OVERLAP_EPS)
            let hitObstacle = false;

            const clampAxis = (proposed, lastSafeVal, size, otherStart, otherSize) => {
                const delta = proposed - lastSafeVal;
                // Judame LINK kliūties IR ją kertame ŠIAME kadre (praeitą kadrą dar nekirtome) —
                // sustabdome tiksliai ties jos kraštu, o ne leidžiame įeiti į vidų.
                if (delta > 0 && proposed + size > otherStart && lastSafeVal + size <= otherStart + EPS) {
                    hitObstacle = true;
                    return otherStart - size;
                }
                if (delta < 0 && proposed < otherStart + otherSize && lastSafeVal >= otherStart + otherSize - EPS) {
                    hitObstacle = true;
                    return otherStart + otherSize;
                }
                return proposed;
            };

            // Kiekviena ašis apkerpama atskirai, tikrinant ar kita kliūtis JAU dengia
            // likusias dvi ašis toje pozicijoje, kur jau esame po ankstesnių apkirpimų.
            others.forEach((otherRect) => {
                const overlapsYZ = proposedY < otherRect.y + otherRect.height && proposedY + boundHeight > otherRect.y
                    && proposedZ < otherRect.z + otherRect.depth && proposedZ + boundDepth > otherRect.z;
                if (overlapsYZ) proposedX = clampAxis(proposedX, lastSafe.x, boundWidth, otherRect.x, otherRect.width);
            });
            others.forEach((otherRect) => {
                const overlapsXZ = proposedX < otherRect.x + otherRect.width && proposedX + boundWidth > otherRect.x
                    && proposedZ < otherRect.z + otherRect.depth && proposedZ + boundDepth > otherRect.z;
                if (overlapsXZ) proposedY = clampAxis(proposedY, lastSafe.y, boundHeight, otherRect.y, otherRect.height);
            });
            others.forEach((otherRect) => {
                const overlapsXY = proposedX < otherRect.x + otherRect.width && proposedX + boundWidth > otherRect.x
                    && proposedY < otherRect.y + otherRect.height && proposedY + boundHeight > otherRect.y;
                if (overlapsXY) proposedZ = clampAxis(proposedZ, lastSafe.z, boundDepth, otherRect.z, otherRect.depth);
            });

            groupRef.current.position.set(proposedX, proposedY, proposedZ);
            lastSafePositionRef.current = { x: proposedX, y: proposedY, z: proposedZ };

            if (hitObstacle) {
                setCollisionFlash(true);
                if (collisionTimerRef.current) clearTimeout(collisionTimerRef.current);
                collisionTimerRef.current = setTimeout(() => setCollisionFlash(false), 400);
                const collidingOther = others.find(r => (
                    proposedX < r.x + r.width && proposedX + boundWidth > r.x &&
                    proposedY < r.y + r.height && proposedY + boundHeight > r.y &&
                    proposedZ < r.z + r.depth && proposedZ + boundDepth > r.z
                ));
                onCollision?.(collidingOther ? collidingOther.furnitureId : furnitureId);
            }
        }
    }, [wallWidth, wallHeight, wallDepth, totalWidth, totalHeight, totalDepth, topPanelExtra, furnitureId, allFurnitureGroupRects, onCollision]);


    const handleTransformStart = () => {
        if (controls) controls.enabled = false;
        setIsDragging(true);
        if (groupRef.current) {
            initialPositionRef.current = { x: groupRef.current.position.x, y: groupRef.current.position.y, z: groupRef.current.position.z };
            lastSafePositionRef.current = { x: groupRef.current.position.x, y: groupRef.current.position.y, z: groupRef.current.position.z };
        }
    };

    const handleTransformEnd = () => {
        if (controls) controls.enabled = true;
        setIsDragging(false);
        if (groupRef.current) {
            const newPos = groupRef.current.position;
            const safeTotalWidth = Math.max(0.001, totalWidth);
            const safeTotalHeight = Math.max(0.001, totalHeight);
            const safeTotalDepth = Math.max(0.001, totalDepth);
            onMove(furnitureId, newPos.x, newPos.y, newPos.z, safeTotalWidth, safeTotalHeight, safeTotalDepth, initialPositionRef.current.x, initialPositionRef.current.y, initialPositionRef.current.z);
        }
    };

    const handleManualPositionChange = (fid, newX, newY, newZ = position.z) => {
         onMove(fid, newX, newY, newZ, totalWidth, totalHeight, totalDepth, position.x, position.y, position.z);
    };

    const localBasisType = cabinets[0]?.basisType ?? cabinetProps.basisType ?? 0;
    const localBasisHeight = cabinets[0]?.basisHeight ?? cabinetProps.basisHeight ?? 0.1;
    // ClickUp "2.0 Baldas. Karkaso gylis. Pagrindai/Šonai turi būti 60cm (be durų),
    // dabar 58.2cm" + "3.0 Išorė... durų uždėjimo taisyklė" — SingleCabinet3D.jsx
    // carcassPanelToggleProps dabar bottom/left/right irgi sąlygiškai atsitraukia
    // TIK kai TAS kabinetas turi dureles (kaip jau veikia 'top') — UnifiedBase.jsx
    // Cokolio juosta (FRONT_RECESS) TURI judėti SINCHRONIŠKAI su šiuo, kitaip
    // atgaivintume senąjį Z-laiptelio bug'ą ("pridėjus/atėmus dureles, keičiasi
    // pagrindo išvaizdavimas" — žr. UnifiedBase.jsx komentarą). Kadangi Cokolis yra
    // VIENA bendra grupės juosta (ne po kabinetą), naudojame "ar BENT VIENAS šios
    // grupės kabinetas turi dureles" kaip bendrą sinchronizacijos signalą — žinoma
    // riba: mišrioje grupėje (vienas korpusas su durimis, kitas be) PATS tas kabinetas,
    // kuris be durų, vis tiek nesutaptų su Cokoliu (jis atsitrauktų, nes KITAS grupės
    // narys turi dureles) — tai PRIIMTINA kompromisas, nes vartotojas aiškiai nurodė,
    // kad gylio taisyklė "galioja tik vienam korpusui", o Cokolis architektūriškai
    // negali būti skaidomas po kabinetą be didesnio pertvarkymo.
    const anyDoorInGroup = cabinets.some((cab, idx) => {
        const prefix = `${cab.furnitureId}-${idx}-`;
        return Object.keys(cabinetProps.doorColors || {}).some((k) => k.startsWith(prefix) && cabinetProps.doorColors[k]);
    });

    const handleHintPointerDown = useCallback((e) => {
        e.stopPropagation();
        if (controls) controls.enabled = false;

        const startPosX = groupRef.current ? groupRef.current.position.x : position.x;
        const startPosY = groupRef.current ? groupRef.current.position.y : position.y;
        const startPosZ = groupRef.current ? groupRef.current.position.z : position.z;

        const wallWidthMeters = (wallWidth || 600) / 100;
        const wallHeightMeters = (wallHeight || 250) / 100;

        // Raycast onto a fixed Z plane at the furniture's front face.
        // This gives accurate world coords regardless of camera angle or zoom.
        const dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -(startPosZ + totalDepth / 2));
        const rect = gl.domElement.getBoundingClientRect();
        const raycaster = new THREE.Raycaster();
        const hitPoint = new THREE.Vector3();

        const getWorldPoint = (clientX, clientY) => {
            const ndc = new THREE.Vector2(
                ((clientX - rect.left) / rect.width) * 2 - 1,
                -((clientY - rect.top) / rect.height) * 2 + 1
            );
            raycaster.setFromCamera(ndc, camera);
            return raycaster.ray.intersectPlane(dragPlane, hitPoint) ? hitPoint.clone() : null;
        };

        const startWorld = getWorldPoint(e.clientX, e.clientY);
        if (!startWorld) { if (controls) controls.enabled = true; return; }

        // Offset keeps the grab point fixed under the cursor
        const offsetX = startPosX - startWorld.x;
        const offsetY = startPosY - startWorld.y;

        setIsDragging(true);

        const onMouseMove = (moveE) => {
            const world = getWorldPoint(moveE.clientX, moveE.clientY);
            if (!world || !groupRef.current) return;
            let proposedX = Math.max(0, Math.min(world.x + offsetX, wallWidthMeters - totalWidth));
            let proposedY = Math.max(0, Math.min(world.y + offsetY, wallHeightMeters - totalHeight));

            // Ta pati "atsiremia į kaimyno kraštą" logika, kaip handleTransformChange
            // aukščiau — ši (paprastesnė, tik X/Y — Z nesikeičia) tempimo rankena anksčiau
            // visai netikrindavo kaimyninių baldų per patį vilkimą, tad baldas galėdavo
            // laikinai "užvilkti" ant kito, kol dar nepaleistas pelės mygtukas (žr.
            // /admin/bugs "Neturi būti galimybės su pelyte stumiant baldą... užliptų
            // ant kito baldo viršaus").
            const boundWidth = totalWidth + topPanelExtra.width;
            const boundHeight = totalHeight + topPanelExtra.height;
            const boundDepth = totalDepth + topPanelExtra.depth;
            const others = allFurnitureGroupRects.filter(r => r.furnitureId !== furnitureId);
            const lastSafe = lastSafePositionRef.current;
            const EPS = 0.001;
            const clampAxis = (proposed, lastSafeVal, size, otherStart, otherSize) => {
                const delta = proposed - lastSafeVal;
                if (delta > 0 && proposed + size > otherStart && lastSafeVal + size <= otherStart + EPS) return otherStart - size;
                if (delta < 0 && proposed < otherStart + otherSize && lastSafeVal >= otherStart + otherSize - EPS) return otherStart + otherSize;
                return proposed;
            };
            others.forEach((otherRect) => {
                const overlapsYZ = proposedY < otherRect.y + otherRect.height && proposedY + boundHeight > otherRect.y
                    && startPosZ < otherRect.z + otherRect.depth && startPosZ + boundDepth > otherRect.z;
                if (overlapsYZ) proposedX = clampAxis(proposedX, lastSafe.x, boundWidth, otherRect.x, otherRect.width);
            });
            others.forEach((otherRect) => {
                const overlapsXZ = proposedX < otherRect.x + otherRect.width && proposedX + boundWidth > otherRect.x
                    && startPosZ < otherRect.z + otherRect.depth && startPosZ + boundDepth > otherRect.z;
                if (overlapsXZ) proposedY = clampAxis(proposedY, lastSafe.y, boundHeight, otherRect.y, otherRect.height);
            });

            groupRef.current.position.set(proposedX, proposedY, startPosZ);
            lastSafePositionRef.current = { x: proposedX, y: proposedY, z: startPosZ };
        };

        const onMouseUp = () => {
            if (controls) controls.enabled = true;
            setIsDragging(false);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            if (groupRef.current) {
                const p = groupRef.current.position;
                onMove(furnitureId, p.x, p.y, p.z, totalWidth, totalHeight, totalDepth, startPosX, startPosY, startPosZ);
            }
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [controls, camera, gl, position, furnitureId, wallWidth, wallHeight, totalWidth, totalHeight, totalDepth, topPanelExtra, allFurnitureGroupRects, onMove]);

    const btnStyle = {
        background: '#111', color: 'white', border: 'none', borderRadius: tokens.borderRadiusLarge,
        width: '22px', height: '22px', cursor: 'pointer', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '300',
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)', pointerEvents: 'auto', lineHeight: 1,
        transition: 'background 0.15s',
    };
    const removeBtnStyle = { ...btnStyle, background: '#c0392b' };
    const highlightColor = isActive ? new THREE.Color(0x09aa89) : null;
    const showTransformControls = false; // TransformControls disabled — drag hint used instead
    const isDisabled = !isActive && activeStep !== 'Patalpa';

    // PATAISYTA (žr. identišką SingleCabinet3D.jsx currentColor komentarą): Pagrindas/
    // Stogelis/klaidingų-šonų dėžės dabar IRGI rodo TIKRĄ pasirinktos dekoro plokštės
    // spalvą (ta pati cabinetProps.selectedPlate.color, kokia jau naudojama plateColor
    // žemiau kitam tikslui), ne vien bendrinę pilką — vartotojas: "Karkasas, durelės ir
    // visos kitos plokstes turi buti vienodos spalvos". isActive IR TOLIAU rodo
    // paryškinimo pilką spalvą TIK tada, kai tikros spalvos iš viso nėra pasirinkta.
    const rawBaseMatColor = (cabinets[0]?.plateMaterial || cabinetProps.selectedPlate)?.color;
    const validBaseMatColor = (rawBaseMatColor && typeof rawBaseMatColor === 'string' && (rawBaseMatColor.startsWith('#') || rawBaseMatColor.startsWith('rgb'))) ? rawBaseMatColor : null;
    const furnitureBaseColor = validBaseMatColor || (isActive ? "#9e9e9e" : "#d6d6d6");

    // Perduodama kraštiniams (isFirstInGroup/isLastInGroup) SingleCabinet3D egzemplioriams,
    // kad jų durelės susiaurėtų per šį plotį — žr. renderSideElements pastabą žemiau.
    const leftSideElementWidth = (leftSideElement && leftSideElement.type !== 0)
        ? (leftSideElement.type === 2 && leftSideElement.width ? leftSideElement.width / 100 : 0.018)
        : 0;
    const rightSideElementWidth = (rightSideElement && rightSideElement.type !== 0)
        ? (rightSideElement.type === 2 && rightSideElement.width ? rightSideElement.width / 100 : 0.018)
        : 0;

    const cp = coverPlates && coverPlates[furnitureId];

    const renderCoverPlates = () => {
        if (!cp) return null;
        const plates = [];

        if (cp.left && cp.left.enabled) {
            const w = cp.left.width / 100;
            plates.push(
                <Box key="left-cp" position={[-w/2, totalHeight/2, totalDepth/2]} args={[w, totalHeight, totalDepth]}>
                    <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
        }
        if (cp.right && cp.right.enabled) {
            const w = cp.right.width / 100;
            plates.push(
                <Box key="right-cp" position={[totalWidth + w/2, totalHeight/2, totalDepth/2]} args={[w, totalHeight, totalDepth]}>
                    <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
        }
         if (cp.top && cp.top.enabled) {
            // CoverPlates.jsx (tabs/CoverPlates.jsx) saugo šio lauko reikšmę kaip
            // `width` (ta pati "Plotis (cm)" TextField naudojama visoms trims pusėms),
            // ne `height` — su `.height` čia visada gaudavosi undefined/100 = NaN,
            // todėl viršutinė dengianti plokštė virsdavo NaN aukščio Box'u.
            const h = cp.top.width / 100;
            plates.push(
                <Box key="top-cp" position={[totalWidth/2, totalHeight + h/2, totalDepth/2]} args={[totalWidth, h, totalDepth]}>
                    <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
        }
        if (cp.back && cp.back.enabled) {
            const d = cp.back.depth / 100;
            plates.push(
                <Box key="back-cp" position={[totalWidth/2, totalHeight/2, totalDepth + d/2]} args={[totalWidth, totalHeight, d]}>
                    <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
        }
        return plates;
    };

    // Falšjuosta/falšfasadas/papildomas šonas turi ĮEITI į baldo plotį (totalWidth
    // nesikeičia) — durelės sumažėja per šio elemento plotį, o ne baldas išsiplečia
    // už totalWidth ribų. Todėl skydeliai statomi VIDUJE [0, totalWidth] intervalo
    // (kraštinis plotis, kurį atitinkamai "atkanda" durelės — žr. leftSideElementWidth/
    // rightSideElementWidth SingleCabinet3D durelių extend logikoje), o ne už jos.
    // Regis matomas gylis (kiek juosta/šonas išsikiša nuo priekio, ne medžiagos
    // storis) — anksčiau čia buvo naudojamas TAS PATS 0.018 (18mm storis), tad
    // strip'as išeidavo vos 18mm gylio plokštelė, praktiškai nepastebima prie
    // priekio krašto (žr. ClickUp "Neišpiešia falšjuostos/Papildomo šono").
    const SIDE_ELEMENT_VISIBLE_DEPTH = 0.1;
    // Durelių priekinis paviršius yra ties `cabinet.depth + 0.003`, ne lygiai
    // `cabinet.depth` (žr. OuterDoors.jsx doorZPosition = cabinet.depth -
    // doorThickness/2 + 0.003 — tas +0.003 yra sąmoninga apsauga nuo z-fighting
    // su korpuso sienelėmis). Šis skydelis anksčiau baigdavosi lygiai ties
    // totalDepth (be to +0.003), tad likdavo giliau nei durys — 3mm matomas
    // nesutapimas (žr. ClickUp "Falšai ir šonai turėtų būti vienoje plokštumoje
    // kaip durelės. Dabar jie giliau nei durys"). Ta pati +0.003 poslinkis čia
    // suvienodina abiejų priekinius paviršius.
    const FRONT_FACE_DOOR_OFFSET = 0.003;

    // "Falšjuosta"/"Falšfasadas" (SideElementDialog type 1/2) yra sąmoningai negili
    // dekoratyvinė juosta prie priekinio krašto (imituoja durelių plokštumą, ne
    // tikra šoninė sienelė) — jai SIDE_ELEMENT_VISIBLE_DEPTH (10cm) tinka. Bet
    // "Papildomas šonas" (type 3) yra TIKRA šoninė plokštė ir turi eiti per VISĄ
    // baldo gylį, ne tik 10cm prie priekio — anksčiau abu naudojo TĄ PATĮ fiksuotą
    // gylį, tad "Papildomas šonas" atrodydavo kaip vos kelių cm skydelis (žr.
    // /admin/bugs "2.2 Šonai. Neišpiešia Papildomo šono").
    const getSideElementDepth = (type) => type === 3 ? totalDepth : SIDE_ELEMENT_VISIBLE_DEPTH;

    const renderSideElements = () => {
        const elements = [];

        if (leftSideElementWidth > 0) {
            const elemW = leftSideElementWidth;
            const elemD = getSideElementDepth(leftSideElement?.type);
            const pos = [elemW/2, totalHeight/2, totalDepth - elemD/2 + FRONT_FACE_DOOR_OFFSET];

            elements.push(
                <Box key="false-left" position={pos} args={[elemW, totalHeight, elemD]}>
                     <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
            // "Rodyti kontūrus" režimas (žr. SingleCabinet3D PanelEdges) anksčiau apimdavo
            // visus baldo skydelius/dureles, IŠSKYRUS šį netikrą šoninį elementą (falšjuosta/
            // falšfasadas/papildomas šonas) — jis likdavo be kontūro, kad ir kuris pasirinkimas
            // (SideElementDialog) būtų aktyvus. Dabar PanelEdges pridėta ir jam, kaip ir
            // visiems kitiems skydeliams (žr. "2.2 Baldas. Kairys/Dešinys šonas" užduotį).
            if (cabinetProps.showDoorEdges) {
                elements.push(<PanelEdges key="false-left-edges" args={[elemW, totalHeight, elemD]} position={pos} />);
            }
        }

        if (rightSideElementWidth > 0) {
            const elemW = rightSideElementWidth;
            const elemD = getSideElementDepth(rightSideElement?.type);
            const pos = [totalWidth - elemW/2, totalHeight/2, totalDepth - elemD/2 + FRONT_FACE_DOOR_OFFSET];

            elements.push(
                <Box key="false-right" position={pos} args={[elemW, totalHeight, elemD]}>
                     <meshStandardMaterial color={furnitureBaseColor} />
                </Box>
            );
            if (cabinetProps.showDoorEdges) {
                elements.push(<PanelEdges key="false-right-edges" args={[elemW, totalHeight, elemD]} position={pos} />);
            }
        }
        return elements;
    };


    return (
        <>
            {showTransformControls && (
                <TransformControls
                    object={groupRef}
                    mode="translate"
                    showZ={true}
                    translationSnap={0.01}
                    onMouseDown={handleTransformStart}
                    onMouseUp={handleTransformEnd}
                    onChange={handleTransformChange}
                    size={0.45}
                />
            )}
            {/* Matmenų/laisvumo tarpelių rodyklės TYČIA renderinamos ATSKIRAME,
                STATIŠKOS pozicijos `<group>` (naudoja React state `position`,
                NE gyvai per useFrame sekamą groupRef) — ne viduje to paties
                `<group ref={groupRef}>`, kurio poziciją TransformControls/pelės
                tempimas keičia IMPERATYVIAI kiekvieną kadrą. Taip rodyklės/
                linijos LIEKA FIKSUOTOS palei sienas per visą tempimo trukmę ir
                „pašoka“ į naują vietą tik paleidus pelę — juda TIK pats baldo
                3D modelis (žr. /admin/bugs "Kai su pelyte judinu baldą, šios
                rodyklės neturi judėti" + "linijos... turi būti fiksuotos palei
                sienas, o judėti tik pats baldas"). */}
            {!snapshotMode && isActive && activeStep === 'Baldas' && (measureMode?.furniture || measureMode?.clearance) && (
                <group position={[position.x, position.y, position.z]}>
                    {measureMode?.furniture && (
                        <FurnitureMeasurementsOverlay
                            totalWidth={totalWidth} totalHeight={totalHeight} totalDepth={totalDepth}
                            onDimensionChange={onDimensionChange} furnitureId={furnitureId}
                            disabled={isDisabled} modalOpen={modalOpen}
                            displayZ={measurementsZ} wallWidth={wallWidth}
                            position={position} pixelOffsetM={pixelOffsetM} depthPixelOffsetM={depthPixelOffsetM} floorPixelOffsetM={floorPixelOffsetM} depthRowOffsetM={depthRowOffsetM} marginZLocal={marginZLocal}
                            hideInnerArrows={!!measureMode?.furniture && !!measureMode?.clearance}
                        />
                    )}
                    {measureMode?.clearance && (
                        <ClearanceOverlay
                            totalWidth={totalWidth} totalHeight={totalHeight} totalDepth={totalDepth}
                            wallWidth={wallWidth} wallHeight={wallHeight} wallDepth={wallDepth}
                            onMoveXYZ={(newX, newY, newZ) => handleManualPositionChange(furnitureId, newX, newY, newZ)}
                            modalOpen={modalOpen} displayZ={measurementsZ}
                            position={position} pixelOffsetM={pixelOffsetM} floorPixelOffsetM={floorPixelOffsetM} marginZLocal={marginZLocal}
                            hideInnerArrows={!!measureMode?.furniture && !!measureMode?.clearance}
                            groupRef={groupRef}
                        />
                    )}
                </group>
            )}
            <group
                ref={groupRef}
                position={[position.x, position.y, position.z]}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isActive) onSelect(furnitureId);
                    // ClickUp 3.0 "Sunku nuimti pertvarų/lentynų pažymėjimus" (2026-10-26):
                    // kiekvienas konkretus pertvaros/lentynos paspaudimas (SingleCabinet3D.jsx
                    // onShelfClick/onDividerSelect taikiniai) jau sustabdo sklaidą ties savimi,
                    // tad ČIA pasiekia TIK paspaudimai KITUR ant šio baldo (kita plokštė, durys,
                    // fasadas ir pan.) — saugu besąlygiškai išvalyti pažymėjimą.
                    if (onClearSelections) onClearSelections();
                }}
            >

                {/* #14 Dim overlay — when another furniture group is active */}
                {!isActive && activeFurnitureId != null && (
                    <Box
                        args={[totalWidth + 0.01, totalHeight + 0.01, totalDepth + 0.02]}
                        position={[totalWidth / 2, totalHeight / 2, totalDepth / 2]}
                    >
                        <meshStandardMaterial color="#888888" transparent opacity={0.32} depthWrite={false} />
                    </Box>
                )}

                {/* Collision flash overlay */}
                {(collisionFlash || isColliding) && (
                    <Box args={[totalWidth, totalHeight, totalDepth]} position={[totalWidth / 2, totalHeight / 2, totalDepth / 2]}>
                        <meshStandardMaterial color="#e53935" transparent opacity={0.35} />
                    </Box>
                )}

                {isActive && activeStep === 'Baldas' && (
                    <Html position={[totalWidth / 2, totalHeight + 0.35, 0]} center>
                        <OverlayBtn grab cursor="move" onPointerDown={handleHintPointerDown} title="Tempkite norėdami perkelti">
                            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: tokens.fontSizeBase300 }}>open_with</span>
                        </OverlayBtn>
                    </Html>
                )}

                {isActive && (() => {
                    const widthCm = totalWidth * 100;
                    const heightCm = totalHeight * 100;
                    const depthCm = totalDepth * 100;
                    const wallWCm = wallWidth || 600;
                    const wallHCm = wallHeight || 250;
                    const warns = [];
                    if (widthCm > wallWCm + 0.5) warns.push({ msg: `Plotis (${Math.round(widthCm)} cm) viršija sieną (${Math.round(wallWCm)} cm)`, color: '#c53030' });
                    else if (widthCm < 29.5) warns.push({ msg: 'Per siauras (min. 30 cm)', color: '#c05621' });
                    if (heightCm > wallHCm + 0.5) warns.push({ msg: `Aukštis (${Math.round(heightCm)} cm) viršija sieną (${Math.round(wallHCm)} cm)`, color: '#c53030' });
                    else if (heightCm > 276) warns.push({ msg: 'Aukštis >276 cm, (skaidykite į kelis korpusus)', color: '#c05621' });

                    if (warns.length === 0) return null;
                    return (
                        <Html position={[totalWidth / 2, totalHeight + 0.42, totalDepth / 2]} center>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {warns.map((w, i) => (
                                    <div key={i} style={{
                                        background: w.color, color: '#fff', borderRadius: 6, padding: '3px 10px',
                                        fontSize: 10, fontFamily: 'sans-serif', textAlign: 'center',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.25)', pointerEvents: 'none', whiteSpace: 'nowrap',
                                    }}>⚠ {w.msg}</div>
                                ))}
                            </div>
                        </Html>
                    );
                })()}

                {renderCoverPlates()}
                {renderSideElements()}

                {/* Pagrindas/viršus TURI baigtis ties realiu (susiaurėjusiu) korpusu, o ne ties
                    nominaliu totalWidth — kitaip, kai pasirinktas Falšfasadas/Falšjuosta/
                    Papildomas šonas, pagrindas "užlipdavo" ant netikro šoninio elemento
                    (žr. "2.0 Baldas. Pagrindas" — carcassOffsetL/R SingleCabinet3D jau
                    susiaurina PATĮ korpusą, bet pagrindas/viršus to nepaisydavo). */}
                <group position={[(totalWidth + leftSideElementWidth - rightSideElementWidth) / 2, 0, totalDepth / 2]}>
                    <UnifiedBase
                        type={localBasisType}
                        totalWidth={Math.max(0.001, totalWidth - leftSideElementWidth - rightSideElementWidth)}
                        height={localBasisHeight}
                        depth={totalDepth}
                        wallThickness={Number(cabinetProps.wallThickness) || 0.018}
                        color={furnitureBaseColor}
                        showEdges={cabinetProps.showDoorEdges}
                        anyDoorInGroup={anyDoorInGroup}
                        // "Ištraukti į priekį (fasadų plokštuma)" / "Grąžinti į karkaso plokštumą" —
                        // ta pati panelFrontOffsets/onTogglePanelFront mechanika, kokią jau naudoja
                        // SingleCabinet3D.jsx karkaso plokštėms (žr. ten carcassPanelToggleProps),
                        // dabar pritaikyta VISIEMS UnifiedBase matomiems elementams (priekinė
                        // juosta, šoninės cokolio dėžės, kojelės, grindų plokštė — žr. ten
                        // elemProps), ne tik vienintelei priekinei juostai kaip anksčiau. Raktai
                        // grupės-lygio, ne po-kabinetą — "base" vietoj skaitinio cabinetIndex
                        // niekada nesusikirs su tikru kabineto indeksu.
                        activeStep={activeStep}
                        furnitureId={furnitureId}
                        selectedDividerKey={selectedDividerKey}
                        onDividerSelect={onDividerSelect}
                        panelFrontOffsets={cabinetProps.panelFrontOffsets}
                        onTogglePanelFront={cabinetProps.onTogglePanelFront}
                    />
                </group>

                {/* Stogelis — ATSKIRAS UnifiedTopPanel kiekvienam to paties aukščio gretimų
                    kabinetų run'ui (žr. topPanelRuns aukščiau), ne vienas bendras grupės
                    pločio/aukščio kvietimas kaip anksčiau — kitaip žemesnio run'o danga
                    kabodavo ore virš jo paties kabinetų (žr. vartotojo pranešimą). Kiekvienas
                    run'as gauna SAVO poziciją/plotį šio kabineto-koordinačių (ne centro)
                    sistemoje — isGroupLeftEdge/isGroupRightEdge sako UnifiedTopPanel.jsx,
                    ar ŠIS run'as liečia TIKRĄ (ne vidinį, run'ų tarpusavio) baldo kraštą,
                    kad tipų 1/5/6/7 flush/inset elgtųsi TIK ten, o vidinės run'ų ribos
                    visada liktų lygios (be tarpo/laiptelio). */}
                {topPanelRuns.map((run, runIdx) => {
                    const isFirstRun = run.startIdx === 0;
                    const isLastRun = run.endIdx === cabinets.length - 1;
                    const runLeftTrim = isFirstRun ? leftSideElementWidth : 0;
                    const runRightTrim = isLastRun ? rightSideElementWidth : 0;
                    const runEffectiveWidth = Math.max(0.001, run.width - runLeftTrim - runRightTrim);
                    const runCenterX = run.offsetX + runLeftTrim + runEffectiveWidth / 2;
                    return (
                        <group key={`toprun-${runIdx}`} position={[runCenterX, 0, totalDepth / 2]}>
                            <UnifiedTopPanel
                                type={topPanelType}
                                totalWidth={runEffectiveWidth}
                                totalDepth={totalDepth}
                                wallThickness={Number(cabinetProps.wallThickness) || 0.018}
                                topY={run.height}
                                height={topPanelHeight}
                                color={furnitureBaseColor}
                                showEdges={cabinetProps.showDoorEdges}
                                isGroupLeftEdge={isFirstRun}
                                isGroupRightEdge={isLastRun}
                                anyDoorInGroup={anyDoorInGroup}
                                // "Ištraukti į priekį (fasadų plokštuma)" / "Grąžinti į karkaso plokštumą" —
                                // ta pati mechanika, kokią UnifiedBase (žr. aukščiau) jau gavo grupės lygiu;
                                // dabar pritaikyta ir Stogeliui (žr. UnifiedTopPanel.jsx topElemKey). TA PATI
                                // panelFrontOffsets raktas VISIEMS run'ams — fasado perjungimas lieka
                                // vienas bendras visai dangai, kaip topPanelType/topPanelHeight (tik
                                // AUKŠTIS, ne pats tipas/fasado būsena, seka kiekvieno run'o kabinetus).
                                activeStep={activeStep}
                                furnitureId={furnitureId}
                                selectedDividerKey={selectedDividerKey}
                                onDividerSelect={onDividerSelect}
                                panelFrontOffsets={cabinetProps.panelFrontOffsets}
                                onTogglePanelFront={cabinetProps.onTogglePanelFront}
                            />
                        </group>
                    );
                })}

                {cabinets.map((cabinet, idx) => {
                    const uniqueCabinetId = cabinet.uniqueId;
                    const cabinetOffsetX = cabinetOffsetsX[idx];
                    return (
                        <group key={uniqueCabinetId} position={[cabinetOffsetX, 0, 0]}>


                            <SingleCabinet // Naudojamas SingleCabinet komponentas
                                cabinet={cabinet}
                                index={idx}
                                cabinets={cabinets} // <-- Perduodami visi cabinets grupėje, kad SingleCabinet3D galėtų nustatyti poziciją
                                lowDetail={lowDetail}
                                {...cabinetProps}
                                isFirstInGroup={idx === 0}
                                isLastInGroup={idx === cabinets.length - 1}
                                leftSideElementWidth={idx === 0 ? leftSideElementWidth : 0}
                                rightSideElementWidth={idx === cabinets.length - 1 ? rightSideElementWidth : 0}
                                basisType={localBasisType}
                                basisHeight={localBasisHeight}
                                // Kai Stogelis aktyvus, UnifiedTopPanel (žr. aukščiau) jau piešia VIENĄ
                                // dengiančią plokštę per visą grupę — kiekvieno atskiro cabinet savo
                                // "top" CabinetPanel taip pat visada piešiamas, tad be šio slopinimo
                                // matytųsi DVI viršutinės plokštės vienoje vietoje (žr. vartotojo "Ten
                                // yra piesiamas naujas stogelis" — turi būti TIK viena plokštė).
                                hideTopPanel={topPanelType > 0}
                                topPanelType={topPanelType}
                                topPanelHeight={topPanelHeight}
                                plateMaterial={cabinet.plateMaterial || cabinetProps.selectedPlate}
                                doorPlateMaterial={cabinet.doorPlateMaterial || cabinetProps.selectedPlate}
                                isSelected={isActive && selectedCabinetIndex === idx}
                                furnitureActive={isActive}
                                onCabinetClick={(cabinetIndexInGroup) => { onCabinetClick(cabinetIndexInGroup); }}
                                onShelfHeightChange={onShelfHeightChange}
                                onShelfDelete={onShelfDelete}
                                onSectionDelete={onSectionDelete}
                                activeStep={activeStep}
                                readOnly={readOnly}
                                onSelect={onSelect}
                                wallWidth={wallWidth}
                                wallHeight={wallHeight}
                                backWall={cabinet.backWall !== undefined ? cabinet.backWall : cabinetProps.backWall}
                                onRemoveDoors={onRemoveDoors}
                                onCreateDoors={onCreateDoors}
                                onCabinetWidthChange={onCabinetWidthChange}
                                onInterCabinetResize={onInterCabinetResize ? (leftId, rightId, newLW, newRW) => onInterCabinetResize(furnitureId, leftId, rightId, newLW, newRW) : undefined}
                                prevCabinet={idx > 0 ? cabinets[idx - 1] : null}
                                onCabinetHeightChange={onCabinetHeightChange}
                                measureMode={measureMode}
                                selectedShelfIdx={selectedShelfBoard?.furnitureId === furnitureId && selectedShelfBoard?.uid === cabinet.uniqueId ? selectedShelfBoard.idx : null}
                                onSelectShelf={(idx) => onSetSelectedShelfBoard?.(idx !== null ? { furnitureId, uid: cabinet.uniqueId, idx } : null)}
                                splitSectionMode={splitSectionMode}
                                selectedShelf={selectedShelf}
                                onHorizontalSplit={onHorizontalSplit}
                                onVerticalSplit={onVerticalSplit}
                                onSectionRatioChange={onSectionRatioChange}
                                onLeafSectionDelete={onLeafSectionDelete}
                                onNarySectionResize={onNarySectionResize}
                                selectedDividerKey={selectedDividerKey}
                                onDividerSelect={onDividerSelect}
                                onOpenOuterDialog={onOpenOuterDialog}
                                onOpenInnerDialog={onOpenInnerDialog}
                                mergedSections={mergedSections}
                                mergeMode={mergeMode}
                                onToggleMergeMode={onToggleMergeMode}
                                onDetachSection={onDetachSection}
                                onInteriorHSplit={onInteriorHSplit}
                                onInteriorVSplit={onInteriorVSplit}
                                onInteriorSectionResize={onInteriorSectionResize}
                                onInteriorSectionDelete={onInteriorSectionDelete}
                                onInteriorLeafSectionDelete={onInteriorLeafSectionDelete}
                                onInteriorNarySectionResize={onInteriorNarySectionResize}
                                onPromoteAtPanel={onPromoteAtPanel}
                                onDemoteAtPanel={onDemoteAtPanel}
                            />
                        </group>
                    );
                })}

                {/* Sliding doors overlay for furniture type 1 — TURI BŪTI groupRef grupės
                    VIDUJE (ne šalia), nes tempimo metu (mouse drag) pozicija atnaujinama
                    IMPERATYVIAI kiekvieną kadrą tiesiai per groupRef.current.position, be
                    React re-render'o (žr. komentarą aukščiau apie TransformControls). Kai
                    šis overlay buvo ŠALIA (sibling po </group>), jis skaitė tik stat`inį
                    (tarp re-renderių pasenusį) `position` prop'ą, tad tempiant pelyte
                    durys likdavo vietoje, o korpusas judėdavo — durys "atsiplėšdavo" nuo
                    korpuso (žr. /admin/bugs "durys visada turi buti prisirisusios prie
                    karkaso"). Būdamas grupės viduje, overlay automatiškai paveldi tą pačią
                    imperatyviai atnaujinamą transformaciją kaip ir patys kabinetai.
                */}
                {furnitureType === 1 && (activeStep === 'Išorė' || activeStep === 'Baldas') && (
                    <SlidingDoorsOverlay
                        totalWidth={totalWidth}
                        totalHeight={totalHeight}
                        totalDepth={totalDepth}
                        doorCount={Math.max(2, slidingDoorCount || 2)}
                        plateColor={cabinetProps.selectedPlate?.color || '#dddddd'}
                        profile={slidingDoorProfile ?? 0}
                        activeStep={activeStep}
                    />
                )}
            </group>
        </>
    );
};

export default FurnitureGroup;
